import React, { useState, useContext, useCallback, useEffect, useRef } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Avatar,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Stack,
  CircularProgress,
  Backdrop,
  useTheme,
  alpha,
  InputAdornment,
  Autocomplete,
  Switch,
  FormControlLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  Diamond as DiamondIcon,
  Scale as ScaleIcon,
  AttachMoney as AttachMoneyIcon,
  Tag as TagIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  ShortText as ShortTextIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  PriceCheck as PriceCheckIcon
} from '@mui/icons-material';
import { DataContext } from '../../context/DataContext';
import { styled } from '@mui/material/styles';
import QRCode from 'qrcode';
import { useReactDevTools } from '../../context/ReactDevTools';

// StyledTextField for regular text fields
const StyledTextField = styled(TextField)(({ theme, fullWidth }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 20,
    backdropFilter: 'blur(10px)',
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.3)
      : alpha('#ffffff', 0.8),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.5)
        : alpha('#ffffff', 0.9),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.paper, 0.5)
        : alpha('#ffffff', 0.9),
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b',
  },
  mb: 2,
  width: fullWidth ? '100%' : '300px',
}));

// Gross Weight Product Toggle
const GrossWeightToggle = styled(Paper)(({ theme, isDark }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(
    isDark ? theme.palette.background.paper : '#ffffff',
    isDark ? 0.3 : 0.8
  ),
  border: `1px solid ${alpha(
    isDark ? theme.palette.divider : '#e2e8f0',
    isDark ? 0.2 : 0.3
  )}`,
  marginBottom: theme.spacing(2),
  '&:hover': {
    borderColor: alpha('#8B5CF6', isDark ? 0.4 : 0.3),
  },
}));

// Helper function for formatting numbers
const formatNumber = (num) => {
  if (!num || num === "" || Number.isNaN(num) || num === "NaN") return "0";
  return parseFloat(num).toFixed(2);
};

const AddItemPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isSecretMode } = useReactDevTools();

  const {
    stock,
    loading,
    suppliers,
    companyDetails,
    updateCompanyDetails,
    grossStock,
    getGrossStock,
    addGrossStockItem,
    addStockTransaction,
    addBulkStock,
  } = useContext(DataContext);

  const currentCompanyId = companyDetails?.[0]?.id;

  // State for dynamic item categories, metals, and silver types
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);
  const [silverTypes, setSilverTypes] = useState([]);

  const defaultSilverTypes = useCallback(() => [
    { id: '1', name: '999silver', displayName: '999 Silver', shortcut: '999' },
    { id: '2', name: '925silver', displayName: '925 Silver', shortcut: '925' },
    { id: '3', name: 'mixsilver', displayName: 'Mix Silver', shortcut: 'MIX' },
    { id: '4', name: 'silver', displayName: 'Silver', shortcut: 'SIL' },
    { id: '5', name: 'oldsilver', displayName: 'Old Silver', shortcut: 'OLD' }
  ], []);

  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [metalDialogOpen, setMetalDialogOpen] = useState(false);
  const [silverTypeDialogOpen, setSilverTypeDialogOpen] = useState(false);

  // Form states for add/edit
  const [newItemName, setNewItemName] = useState('');
  const [newItemShortcut, setNewItemShortcut] = useState('');
  const [newMetalName, setNewMetalName] = useState('');
  const [newMetalShortcut, setNewMetalShortcut] = useState('');
  const [newSilverType, setNewSilverType] = useState('');
  const [newSilverTypeDisplayName, setNewSilverTypeDisplayName] = useState('');
  const [newSilverTypeShortcut, setNewSilverTypeShortcut] = useState('');

  // Edit states
  const [editingItem, setEditingItem] = useState(null);
  const [editingMetal, setEditingMetal] = useState(null);
  const [editingSilverType, setEditingSilverType] = useState(null);

  // Loading states
  const [isUpdatingMasterData, setIsUpdatingMasterData] = useState(false);

  const [, setGeneratedQR] = useState(null);
  const [, setQrImage] = useState('');
  const [qrImages, setQrImages] = useState({});
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dynamicFields, setDynamicFields] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [isGrossWeightProduct, setIsGrossWeightProduct] = useState(false);
  const [isMRPMode, setIsMRPMode] = useState(false);

  // Multiple item weights state with serial numbers
  const [itemWeights, setItemWeights] = useState([
    { serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }
  ]);

  // ========= GROSS STOCK STATES =========
  const [selectedGrossItem, setSelectedGrossItem] = useState(null);
  const [isAddingNewItem, setIsAddingNewItem] = useState(false);
  const [newGrossItemName, setNewGrossItemName] = useState('');
  const [newGrossItemMetal, setNewGrossItemMetal] = useState('');
  const [isGrossItemBased, setIsGrossItemBased] = useState(true);
  const [grossOperation, setGrossOperation] = useState('add');
  const submittingRef = useRef(false);

  // Serial validation errors state
  const [serialValidationErrors, setSerialValidationErrors] = useState({
    hasEmptySerial: false,
    hasDuplicates: false,
    duplicateNumbers: []
  });

  // ========= UTILITY FUNCTIONS =========
  const safeParseJSON = (data, fallback = []) => {
    if (!data) return fallback;
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        if (data.trim() === '' || data.trim() === 'null' || data.trim() === 'undefined') {
          return fallback;
        }
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch (error) {
        console.error('Error parsing JSON:', error, 'Data:', data);
        return fallback;
      }
    }
    return fallback;
  };

  const toCapitalCase = useCallback((str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);

  const toUpperCase = useCallback((str) => {
    return str ? str.toUpperCase() : '';
  }, []);

  const findFirstMissingNumber = useCallback((arr) => {
    if (arr.length === 0) return 1;
    const numberSet = new Set(arr);
    let i = 1;
    while (numberSet.has(i)) {
      i++;
    }
    return i;
  }, []);

  const getNextSerialNumber = useCallback((itemName, metal) => {
    if (!itemName || !metal) return 1;

    const allItemsForType = stock.filter(item =>
      item.name === itemName && item.metal === metal
    );

    if (allItemsForType.length === 0) {
      return 1;
    }

    const unsoldItems = allItemsForType.filter(item => item.sold === "No" || item.sold === false);
    const unsoldSerialNumbers = unsoldItems
      .map(item => parseInt(item.serialNo) || 0)
      .filter(num => !isNaN(num) && num > 0)
      .sort((a, b) => a - b);

    const firstMissing = findFirstMissingNumber(unsoldSerialNumbers);
    return firstMissing;
  }, [stock, findFirstMissingNumber]);

  const checkForDuplicate = useCallback((itemName, metal, serialNo) => {
    if (!stock || stock.length === 0) return false;

    return stock.some(item =>
      item.name === itemName &&
      item.metal === metal &&
      parseInt(item.serialNo) === parseInt(serialNo) &&
      (item.sold === "No" || item.sold === false)
    );
  }, [stock]);

  const assignSerialNumbers = useCallback((itemName, metal, weights) => {
    if (!itemName || !metal) {
      return weights.map((w, index) => ({
        ...w,
        serialNo: (index + 1).toString()
      }));
    }

    const existingSerials = stock
      .filter(item => item.name === itemName && item.metal === metal)
      .map(item => parseInt(item.serialNo))
      .filter(num => !isNaN(num) && num > 0)
      .sort((a, b) => a - b);

    const missingNumbers = [];
    if (existingSerials.length > 0) {
      let maxSerial = Math.max(...existingSerials);
      for (let i = 1; i <= maxSerial; i++) {
        if (!existingSerials.includes(i)) {
          missingNumbers.push(i);
        }
      }
      let nextNumber = maxSerial + 1;

      const assignedWeights = weights.map((w, index) => {
        let serialNo;
        if (index < missingNumbers.length) {
          serialNo = missingNumbers[index];
        } else {
          serialNo = nextNumber + (index - missingNumbers.length);
        }
        return {
          ...w,
          serialNo: serialNo.toString()
        };
      });

      return assignedWeights;
    } else {
      return weights.map((w, index) => ({
        ...w,
        serialNo: (index + 1).toString()
      }));
    }
  }, [stock]);

  const formatGemstoneWeight = useCallback((weight) => {
    if (!weight) return '0.000 carats';
    const num = parseFloat(weight);
    if (isNaN(num)) return '0.000 carats';

    if (num < 1) {
      const cents = Math.round(num * 100);
      return `${cents} cent${cents !== 1 ? 's' : ''}`;
    } else {
      return `${num.toFixed(3)} carats`;
    }
  }, []);

  const updateDynamicFields = useCallback((itemName) => {
    let fields = {};

    const earringCategories = [
      'Tops',
      'Jumka',
      'Bali',
      'Dul',
      'Basket Bali',
      'Sui Dhaaga',
      'Jala'
    ];

    switch (itemName) {
      case 'Finger Ring':
      case 'Bangles/Kada':
      case 'Bracelet':
        fields = {
          type: 'select',
          name: 'itemDetail',
          label: 'Category',
          options: [
            { value: 'Ladies', label: 'Ladies' },
            { value: 'Gents', label: 'Gents' },
            { value: 'Baby', label: 'Baby' }
          ]
        };
        break;
      case 'Necklace':
        fields = {
          type: 'select',
          name: 'itemDetail',
          label: 'Necklace Type',
          options: [
            { value: 'Short', label: 'Short' },
            { value: 'Long', label: 'Long' }
          ]
        };
        break;
      case 'Chain':
      case 'Mangalsutra':
        fields = {
          type: 'number',
          name: 'itemDetail',
          label: 'Size (inches)',
          placeholder: 'Size in inches'
        };
        break;
      case 'Earrings':
        fields = {
          type: 'select',
          name: 'itemDetail',
          label: 'Earring Category',
          options: earringCategories.map(cat => ({ value: cat, label: cat }))
        };
        break;
      default:
        fields = {};
    }

    setDynamicFields(fields);
  }, []);

  const handleItemChange = useCallback((itemName, metal, setFieldValue) => {
    if (itemName && metal) {
      try {
        const nextSerial = getNextSerialNumber(itemName, metal);
        setFieldValue('serialNo', nextSerial);
      } catch (error) {
        console.error('Error getting serial number:', error);
      }
    }
  }, [getNextSerialNumber]);

  // ========= WEIGHT FUNCTIONS =========
  const handleAddWeightRow = () => {
    setItemWeights([...itemWeights, { serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
  };

  const handleRemoveWeightRow = (index) => {
    if (itemWeights.length === 1) {
      alert('At least one weight entry is required');
      return;
    }
    const newWeights = itemWeights.filter((_, i) => i !== index);
    setItemWeights(newWeights);
  };

  const handleWeightFieldChange = (index, field, value, setFieldValue) => {
    const newWeights = [...itemWeights];
    newWeights[index][field] = value;

    if (field === 'grossWeight' || field === 'lessWeight') {
      const gross = parseFloat(newWeights[index].grossWeight) || 0;
      const less = parseFloat(newWeights[index].lessWeight) || 0;
      const net = gross - less;
      newWeights[index].netWeight = net >= 0 ? net.toFixed(3) : '0.000';

      if (itemWeights.length === 1) {
        setFieldValue('weight', newWeights[index].netWeight);
        setFieldValue('grossWeight', newWeights[index].grossWeight);
        setFieldValue('lessWeight', newWeights[index].lessWeight);
      }
    }

    setItemWeights(newWeights);

    if (itemWeights.length === 1) {
      if (field === 'grossWeight') {
        setFieldValue('grossWeight', newWeights[0].grossWeight);
      } else if (field === 'lessWeight') {
        setFieldValue('lessWeight', newWeights[0].lessWeight);
      }
    }
  };

  // const calculateFinalPrice = useCallback((values) => {
  //   const { metal, melting, goldCuttingRate, wastage, silverRate, labour } = values;

  //   let weight;
  //   if (itemWeights.length > 0 && itemWeights[0].netWeight) {
  //     weight = parseFloat(itemWeights[0].netWeight) || 0;
  //   } else {
  //     weight = parseFloat(values.weight) || 0;
  //   }

  //   if (!metal || weight <= 0) return 0;

  //   if (metal === 'Gemstone') {
  //     const rate = parseFloat(silverRate) || 0;
  //     return (rate * weight).toFixed(0);
  //   }

  //   let purity = parseFloat(melting) || 0;
  //   if (purity === 91.6) purity = 92;

  //   let finalPrice = 0;

  //   if (['Gold', 'RoseGold', 'WhiteGold'].includes(metal)) {
  //     const rate = parseFloat(goldCuttingRate) || 0;
  //     const wastagePct = parseFloat(wastage) || 0;
  //     finalPrice = (((purity + wastagePct) / 100) * rate * weight).toFixed(0);
  //   } else if (metal === 'Silver') {
  //     if (purity === 99.9 || purity === 999 || purity === 92 || purity === 925 || purity === 92.5) {
  //       purity = 100;
  //     }
  //     const labourNum = parseFloat(labour) || 0;
  //     const silverRateNum = parseFloat(silverRate) || 0;
  //     const wastageNum = parseFloat(wastage) || 0;
  //     finalPrice = ((((purity + wastageNum) / 100) * weight) * (silverRateNum + labourNum)).toFixed(0);
  //   }

  //   return finalPrice;
  // }, [itemWeights]);

  // ========= PERSISTED FORM VALUES =========
  const [persistedFormValues, setPersistedFormValues] = useState({
    supplierName: '',
    supplierShortcut: '',
    name: '',
    metal: '',
    silverType: '',
    melting: '',
    grossWeight: '',
    lessWeight: '',
    weight: '',
    silverRate: '',
    labour: '',
    goldCuttingRate: '',
    wastage: '',
    makingCharges: '',
    serialNo: '',
    description: '',
    huidCodes: [''],
    itemDetail: '',
    miscitemName: '',
    miscitemShortcut: '',
    numberOfPieces: '',
    grossWeightInput: '',
    partyName: '',
    grossDescription: '',
    mrp: '',
    mrpDescription: ''
  });

  const initialValues = persistedFormValues;

  // ========= FETCH DATA =========
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];
      console.log(company, "Company details in AddItemPage");

      const parsedItems = safeParseJSON(company.items);
      setItemCategories(parsedItems);
      console.log('Parsed items:', parsedItems);

      const parsedMetals = safeParseJSON(company.metals);
      setMetalTypes(parsedMetals);
      console.log('Parsed metals:', parsedMetals);

      const parsedSilverTypes = safeParseJSON(company.silverTypes);
      if (parsedSilverTypes.length > 0) {
        setSilverTypes(parsedSilverTypes);
      } else {
        setSilverTypes(defaultSilverTypes);
      }
      console.log('Parsed silver types:', parsedSilverTypes);
    }
  }, [companyDetails, defaultSilverTypes]);

  useEffect(() => {
    const validateSerials = () => {
      const hasEmpty = itemWeights.some(w => !w.serialNo || w.serialNo.trim() === '');
      const serialNos = itemWeights.map(w => parseInt(w.serialNo));
      const duplicates = serialNos.filter((num, index) => serialNos.indexOf(num) !== index);

      setSerialValidationErrors({
        hasEmptySerial: hasEmpty,
        hasDuplicates: duplicates.length > 0,
        duplicateNumbers: duplicates
      });
    };

    validateSerials();
  }, [itemWeights]);

  const [grossStockItems, setGrossStockItems] = useState([]);

  const loadGrossStockItems = useCallback(async () => {
    try {
      await getGrossStock();
    } catch (error) {
      console.error('Error loading gross stock items:', error);
    }
  }, [getGrossStock]);

  useEffect(() => {
    if (isGrossWeightProduct) {
      loadGrossStockItems();
    }
  }, [isGrossWeightProduct, loadGrossStockItems]);

  useEffect(() => {
    if (grossStock && grossStock.length > 0) {
      setGrossStockItems(grossStock);
    }
  }, [grossStock]);

  const fetchSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      if (suppliers && suppliers.length > 0) {
        const formattedSuppliers = suppliers.map(supplier => ({
          id: supplier.id || supplier._id,
          name: supplier.name || supplier.supplierName || '',
          shortcut: supplier.shortcut || supplier.supplierShortcut || ''
        }));
        setSupplierOptions(formattedSuppliers);
      } else {
        const uniqueSuppliers = [...new Set(stock
          .filter(item => item.supplierName)
          .map(item => ({
            name: item.supplierName,
            shortcut: item.supplierShortcut || ''
          }))
        )];

        const seen = new Set();
        const dedupedSuppliers = uniqueSuppliers.filter(supplier => {
          const duplicate = seen.has(supplier.name);
          seen.add(supplier.name);
          return !duplicate;
        });

        setSupplierOptions(dedupedSuppliers);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [suppliers, stock]);

  useEffect(() => {
    fetchSuppliers();
  }, [stock, fetchSuppliers]);

  // ========= UPDATE MASTER DATA =========
  const updateCompanyMasterData = useCallback(async (updatedMasterData) => {
    if (!currentCompanyId) {
      console.error('No company ID found');
      alert('No company selected. Please contact administrator.');
      return { success: false, message: 'No company selected' };
    }

    setIsUpdatingMasterData(true);
    try {
      const currentCompany = companyDetails[0];
      const updatedCompany = {
        ...currentCompany,
        ...updatedMasterData
      };

      const result = await updateCompanyDetails(currentCompanyId, updatedCompany);

      if (result.success) {
        return { success: true, message: 'Master data updated successfully' };
      } else {
        throw new Error(result.message || 'Failed to update master data');
      }
    } catch (error) {
      console.error('Error updating master data:', error);
      return { success: false, message: error.message };
    } finally {
      setIsUpdatingMasterData(false);
    }
  }, [currentCompanyId, companyDetails, updateCompanyDetails]);

  // ========= ITEM CATEGORY FUNCTIONS =========
  const handleAddItemCategory = useCallback(async () => {
    if (!newItemName.trim() || !newItemShortcut.trim()) {
      alert('Please enter both name and shortcut');
      return;
    }

    const newItem = {
      id: Date.now().toString(),
      name: toCapitalCase(newItemName),
      shortcut: toUpperCase(newItemShortcut).slice(0, 3)
    };

    const updatedItems = [...itemCategories, newItem];
    const result = await updateCompanyMasterData({ items: updatedItems });

    if (result.success) {
      setItemCategories(updatedItems);
      setNewItemName('');
      setNewItemShortcut('');
      setItemDialogOpen(false);
      alert('✅ Item category added successfully');
    } else {
      alert('❌ Failed to add item category: ' + result.message);
    }
  }, [newItemName, newItemShortcut, itemCategories, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleUpdateItemCategory = useCallback(async () => {
    if (!newItemName.trim() || !newItemShortcut.trim() || !editingItem) {
      alert('Please enter both name and shortcut');
      return;
    }

    const updatedItems = itemCategories.map(item =>
      item.id === editingItem.id
        ? { ...item, name: toCapitalCase(newItemName), shortcut: toUpperCase(newItemShortcut).slice(0, 3) }
        : item
    );

    const result = await updateCompanyMasterData({ items: updatedItems });

    if (result.success) {
      setItemCategories(updatedItems);
      setNewItemName('');
      setNewItemShortcut('');
      setEditingItem(null);
      setItemDialogOpen(false);
      alert('✅ Item category updated successfully');
    } else {
      alert('❌ Failed to update item category: ' + result.message);
    }
  }, [newItemName, newItemShortcut, editingItem, itemCategories, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleDeleteItemCategory = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this item category?')) {
      return;
    }

    const updatedItems = itemCategories.filter(item => item.id !== id);
    const result = await updateCompanyMasterData({ items: updatedItems });

    if (result.success) {
      setItemCategories(updatedItems);
      alert('✅ Item category deleted successfully');
    } else {
      alert('❌ Failed to delete item category: ' + result.message);
    }
  }, [itemCategories, updateCompanyMasterData]);

  // ========= METAL TYPE FUNCTIONS =========
  const handleAddMetalType = useCallback(async () => {
    if (!newMetalName.trim() || !newMetalShortcut.trim()) {
      alert('Please enter both name and shortcut');
      return;
    }

    const newMetal = {
      id: Date.now().toString(),
      name: toCapitalCase(newMetalName),
      shortcut: toUpperCase(newMetalShortcut).slice(0, 2)
    };

    const updatedMetals = [...metalTypes, newMetal];
    const result = await updateCompanyMasterData({ metals: updatedMetals });

    if (result.success) {
      setMetalTypes(updatedMetals);
      setNewMetalName('');
      setNewMetalShortcut('');
      setMetalDialogOpen(false);
      alert('✅ Metal type added successfully');
    } else {
      alert('❌ Failed to add metal type: ' + result.message);
    }
  }, [newMetalName, newMetalShortcut, metalTypes, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleUpdateMetalType = useCallback(async () => {
    if (!newMetalName.trim() || !newMetalShortcut.trim() || !editingMetal) {
      alert('Please enter both name and shortcut');
      return;
    }

    const updatedMetals = metalTypes.map(metal =>
      metal.id === editingMetal.id
        ? { ...metal, name: toCapitalCase(newMetalName), shortcut: toUpperCase(newMetalShortcut).slice(0, 2) }
        : metal
    );

    const result = await updateCompanyMasterData({ metals: updatedMetals });

    if (result.success) {
      setMetalTypes(updatedMetals);
      setNewMetalName('');
      setNewMetalShortcut('');
      setEditingMetal(null);
      setMetalDialogOpen(false);
      alert('✅ Metal type updated successfully');
    } else {
      alert('❌ Failed to update metal type: ' + result.message);
    }
  }, [newMetalName, newMetalShortcut, editingMetal, metalTypes, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleDeleteMetalType = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this metal type?')) {
      return;
    }

    const updatedMetals = metalTypes.filter(metal => metal.id !== id);
    const result = await updateCompanyMasterData({ metals: updatedMetals });

    if (result.success) {
      setMetalTypes(updatedMetals);
      alert('✅ Metal type deleted successfully');
    } else {
      alert('❌ Failed to delete metal type: ' + result.message);
    }
  }, [metalTypes, updateCompanyMasterData]);

  // ========= SILVER TYPE FUNCTIONS =========
  const handleAddSilverType = useCallback(async () => {
    if (!newSilverType.trim()) {
      alert('Please enter silver type name');
      return;
    }

    const newType = {
      id: Date.now().toString(),
      name: newSilverType.toLowerCase().replace(/\s+/g, ''),
      displayName: newSilverTypeDisplayName || toCapitalCase(newSilverType),
      shortcut: newSilverTypeShortcut ? toUpperCase(newSilverTypeShortcut).slice(0, 3) : newSilverType.substring(0, 3).toUpperCase()
    };

    const updatedSilverTypes = [...silverTypes, newType];
    const result = await updateCompanyMasterData({ silverTypes: updatedSilverTypes });

    if (result.success) {
      setSilverTypes(updatedSilverTypes);
      setNewSilverType('');
      setNewSilverTypeDisplayName('');
      setNewSilverTypeShortcut('');
      setSilverTypeDialogOpen(false);
      alert('✅ Silver type added successfully');
    } else {
      alert('❌ Failed to add silver type: ' + result.message);
    }
  }, [newSilverType, newSilverTypeDisplayName, newSilverTypeShortcut, silverTypes, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleUpdateSilverType = useCallback(async () => {
    if (!newSilverType.trim() || !editingSilverType) {
      alert('Please enter silver type name');
      return;
    }

    const updatedSilverTypes = silverTypes.map(type =>
      type.id === editingSilverType.id
        ? {
          ...type,
          name: newSilverType.toLowerCase().replace(/\s+/g, ''),
          displayName: newSilverTypeDisplayName || toCapitalCase(newSilverType),
          shortcut: newSilverTypeShortcut ? toUpperCase(newSilverTypeShortcut).slice(0, 3) : newSilverType.substring(0, 3).toUpperCase()
        }
        : type
    );

    const result = await updateCompanyMasterData({ silverTypes: updatedSilverTypes });

    if (result.success) {
      setSilverTypes(updatedSilverTypes);
      setNewSilverType('');
      setNewSilverTypeDisplayName('');
      setNewSilverTypeShortcut('');
      setEditingSilverType(null);
      setSilverTypeDialogOpen(false);
      alert('✅ Silver type updated successfully');
    } else {
      alert('❌ Failed to update silver type: ' + result.message);
    }
  }, [newSilverType, newSilverTypeDisplayName, newSilverTypeShortcut, editingSilverType, silverTypes, updateCompanyMasterData, toCapitalCase, toUpperCase]);

  const handleDeleteSilverType = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this silver type?')) {
      return;
    }

    const updatedSilverTypes = silverTypes.filter(type => type.id !== id);
    const result = await updateCompanyMasterData({ silverTypes: updatedSilverTypes });

    if (result.success) {
      setSilverTypes(updatedSilverTypes);
      alert('✅ Silver type deleted successfully');
    } else {
      alert('❌ Failed to delete silver type: ' + result.message);
    }
  }, [silverTypes, updateCompanyMasterData]);

  // ========= VALIDATION SCHEMA =========
  const getValidationSchema = (isGrossWeightMode, isMRPMode) => {
    console.log('Generating validation schema. Gross Weight Mode:', isGrossWeightMode, 'MRP Mode:', isMRPMode);

    if (isGrossWeightMode) {
      return Yup.object({
        metal: Yup.string(),
        silverType: Yup.string(),
        grossWeightInput: Yup.number()
          .typeError('Please enter a valid number')
          .min(0, 'Weight must be positive'),
        numberOfPieces: Yup.number()
          .typeError('Please enter a valid number')
          .min(0, 'Number of pieces must be positive'),
        partyName: Yup.string(),
        grossDescription: Yup.string()
      });
    }

    // MRP Mode validation - keep all fields except weight-related
    if (isMRPMode) {
      return Yup.object({
        supplierName: Yup.string()
          .required('Supplier name is required'),
        supplierShortcut: Yup.string().when('metal', {
          is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema.required('Supplier shortcut is required'),
          otherwise: (schema) => schema
        }),
        name: Yup.string().when('metal', {
          is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema.required('Item name is required'),
          otherwise: (schema) => schema
        }),
        metal: Yup.string().required('Metal type is required'),
        miscitemName: Yup.string().when(['name', 'metal'], {
          is: (name, metal) => name === 'Miscellaneous' && metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema.required('Item Name is required'),
          otherwise: (schema) => schema
        }),
        miscitemShortcut: Yup.string().when(['name', 'metal'], {
          is: (name, metal) => name === 'Miscellaneous' && metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema.required('Item Shortcut is required').max(3, 'Max 3 characters'),
          otherwise: (schema) => schema
        }),
        melting: Yup.number()
          .when('metal', {
            is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
            then: (schema) => schema
              .required('Melting percentage is required')
              .min(0, 'Must be positive')
              .max(100, 'Cannot exceed 100%')
              .typeError('Please enter a valid percentage'),
            otherwise: (schema) => schema
          }),
        serialNo: Yup.number()
          .when('metal', {
            is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
            then: (schema) => schema
              .required('Serial number is required')
              .integer('Must be whole number')
              .min(1, 'Must be at least 1')
              .typeError('Please enter a valid number'),
            otherwise: (schema) => schema
          }),
        mrp: Yup.number()
          .required('MRP is required')
          .min(0.01, 'MRP must be greater than 0')
          .typeError('Please enter a valid number'),
        description: Yup.string(),
        mrpDescription: Yup.string()
      });
    }

    // Original validation for regular mode
    return Yup.object({
      supplierName: Yup.string()
        .required('Supplier name is required'),
      supplierShortcut: Yup.string().when('metal', {
        is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
        then: (schema) => schema.required('Supplier shortcut is required'),
        otherwise: (schema) => schema
      }),
      name: Yup.string().when('metal', {
        is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
        then: (schema) => schema.required('Item name is required'),
        otherwise: (schema) => schema
      }),
      metal: Yup.string().required('Metal type is required'),
      miscitemName: Yup.string().when(['name', 'metal'], {
        is: (name, metal) => name === 'Miscellaneous' && metal !== 'Silver' && metal !== 'Gemstone',
        then: (schema) => schema.required('Item Name is required'),
        otherwise: (schema) => schema
      }),
      miscitemShortcut: Yup.string().when(['name', 'metal'], {
        is: (name, metal) => name === 'Miscellaneous' && metal !== 'Silver' && metal !== 'Gemstone',
        then: (schema) => schema.required('Item Shortcut is required').max(3, 'Max 3 characters'),
        otherwise: (schema) => schema
      }),
      melting: Yup.number()
        .when('metal', {
          is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema
            .required('Melting percentage is required')
            .min(0, 'Must be positive')
            .max(100, 'Cannot exceed 100%')
            .typeError('Please enter a valid percentage'),
          otherwise: (schema) => schema
        }),
      weight: Yup.number()
        .min(0.001, 'Must be at least 0.001')
        .typeError('Please enter a valid number'),
      makingCharges: Yup.number()
        .min(0, 'Cannot be negative')
        .typeError('Please enter a valid number'),
      serialNo: Yup.number()
        .when('metal', {
          is: (metal) => metal !== 'Silver' && metal !== 'Gemstone',
          then: (schema) => schema
            .required('Serial number is required')
            .integer('Must be whole number')
            .min(1, 'Must be at least 1')
            .typeError('Please enter a valid number'),
          otherwise: (schema) => schema
        }),
      wastage: Yup.number()
        .min(0, 'Must be positive')
        .when('metal', {
          is: (metal) => ['Gold', 'RoseGold', 'WhiteGold'].includes(metal),
          then: (schema) => schema.required('Wastage percentage is required for gold items')
            .typeError('Please enter a valid number'),
          otherwise: (schema) => schema
        }),
      silverRate: Yup.number()
        .when('metal', {
          is: (metal) => metal === 'Gemstone',
          then: (schema) => schema
            .required('Rate per carat is required for gemstones')
            .min(0, 'Rate must be positive')
            .typeError('Please enter a valid number'),
          otherwise: (schema) => schema
        }),
      labour: Yup.number()
        .typeError('Please enter a valid number'),
      description: Yup.string(),
      huidCodes: Yup.array().of(Yup.string()),
      itemDetail: Yup.string()
    });
  };

  // ========= VALIDATION BEFORE SUBMIT =========
  const validateBeforeSubmit = (values) => {
    const errors = [];

    if (!values.metal) {
      errors.push('Metal type is required');
    }

    if (values.metal !== 'Silver' && values.metal !== 'Gemstone' && !values.name) {
      errors.push('Item name is required');
    }

    if (!values.supplierName) {
      errors.push('Supplier name is required');
    }

    if (values.metal !== 'Silver' && values.metal !== 'Gemstone' && !values.melting) {
      errors.push('Melting percentage is required');
    }

    const hasEmptySerial = itemWeights.some(w => !w.serialNo || w.serialNo.trim() === '');
    if (hasEmptySerial) {
      errors.push('Please assign serial numbers to all weight entries');
    }

    if (values.metal !== 'Gemstone') {
      const hasEmptyGross = itemWeights.some(w => !w.grossWeight || w.grossWeight.trim() === '');
      if (hasEmptyGross) {
        errors.push('Please enter gross weight for all entries');
      }
    }

    const serialNos = itemWeights.map(w => parseInt(w.serialNo));
    const duplicateSerials = serialNos.filter((num, index) => serialNos.indexOf(num) !== index);
    if (duplicateSerials.length > 0) {
      errors.push(`Duplicate serial numbers: ${duplicateSerials.join(', ')}`);
    }

    for (const weight of itemWeights) {
      const serialNo = parseInt(weight.serialNo);
      if (!isNaN(serialNo) && checkForDuplicate(values.name, values.metal, serialNo)) {
        errors.push(`Serial number ${serialNo} already exists`);
      }
    }

    return errors;
  };

  // ========= GROSS WEIGHT SUBMIT HANDLER =========
  const handleGrossWeightSubmit = async (values, { resetForm, setFieldValue }) => {
    setIsSubmitting(true);
    console.log('Gross Weight Product submission values:', values);

    try {
      let result;

      const pieces = parseFloat(values.numberOfPieces) || 0;
      const weight = parseFloat(values.grossWeightInput) || 0;
      console.log('Quantity - Pieces:', pieces, 'Weight:', weight);

      if (pieces === 0 && weight === 0) {
        alert('Please enter either number of pieces OR weight (or both)');
        setIsSubmitting(false);
        return;
      }

      if (selectedGrossItem) {
        result = await addStockTransaction({
          itemId: selectedGrossItem.id,
          operation: grossOperation,
          numberOfPieces: pieces,
          weightChanged: weight,
          partyName: values.partyName || 'System',
          description: values.grossDescription || `${grossOperation === 'add' ? 'Added' : 'Subtracted'} via Gross Weight Product`
        });
      } else if (isAddingNewItem) {
        if (!newGrossItemName.trim()) {
          alert('Please enter item name');
          setIsSubmitting(false);
          return;
        }

        const itemResult = await addGrossStockItem({
          itemName: newGrossItemName,
          metal: newGrossItemMetal || 'General',
          isPieceBased: isGrossItemBased
        });

        if (!itemResult.success) {
          throw new Error(itemResult.error || 'Failed to create item');
        }

        const newItem = itemResult.data;
        result = await addStockTransaction({
          itemId: newItem.id,
          operation: 'add',
          numberOfPieces: pieces,
          weightChanged: weight,
          partyName: values.partyName || 'System',
          description: values.grossDescription || 'Initial stock via Gross Weight Product'
        });
      } else {
        alert('Please select an existing item or create a new one');
        setIsSubmitting(false);
        return;
      }

      if (result && result.success) {
        alert('✅ Gross Weight Product added successfully!');
        await loadGrossStockItems();
        resetForm();
        setSelectedGrossItem(null);
        setIsAddingNewItem(false);
        setNewGrossItemName('');
        setNewGrossItemMetal('');
        setGrossOperation('add');
        setFieldValue('numberOfPieces', '');
        setFieldValue('grossWeightInput', '');
        setFieldValue('partyName', '');
        setFieldValue('grossDescription', '');
        setPersistedFormValues(prev => ({
          ...prev,
          numberOfPieces: '',
          grossWeightInput: '',
          partyName: '',
          grossDescription: ''
        }));
      } else {
        throw new Error(result?.error || 'Failed to add gross weight product');
      }
    } catch (error) {
      console.error('Error in gross weight submission:', error);
      alert(error.message || 'Failed to add gross weight product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========= HANDLE PRINT LABEL =========
  // ========= HANDLE PRINT LABEL =========
  const handlePrintLabel = (items) => {
    console.log('=== PRINT LABEL DEBUG ===');
    console.log('Items:', items);
    console.log('Is MRP Item:', items?.isMRPItem);

    if (!items) {
      alert('No items to print');
      return;
    }

    let itemsToPrint = [];

    if (Array.isArray(items)) {
      itemsToPrint = items;
    } else if (items.createdItems && Array.isArray(items.createdItems)) {
      itemsToPrint = items.createdItems;
    } else if (items.itemWeights && Array.isArray(items.itemWeights)) {
      const baseItem = items;
      itemsToPrint = baseItem.itemWeights.map((weight, index) => ({
        ...baseItem,
        serialNo: weight.serialNo,
        weight: weight.netWeight,
        grossWeight: weight.grossWeight,
        lessWeight: weight.lessWeight,
        qr: items.qr || `JWL${Date.now()}-${index + 1}`,
        price: baseItem.price || baseItem.mrp || 0,
        isMRPItem: baseItem.isMRPItem || false,
        mrp: baseItem.mrp || baseItem.price || 0,
        melting: baseItem.melting || '0',
        metal: baseItem.metal || '',
        name: baseItem.name || '',
        supplierShortcut: baseItem.supplierShortcut || '',
        makingCharges: baseItem.makingCharges || '',
        description: baseItem.description || baseItem.mrpDescription || ''
      }));
    } else {
      itemsToPrint = [items];
    }

    console.log('Items to print:', itemsToPrint);

    if (itemsToPrint.length === 0) {
      alert('No items to print');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      alert('Please allow popups to print labels');
      return;
    }

    // Helper functions (same as inventory view)
    const formatNumber = (num) => {
      if (!num || num === "") return "0";
      return parseFloat(num).toFixed(3);
    };

    const getMetalShort = (metal) => {
      if (!metal) return '';
      const found = metalTypes.find(m => m.name === metal);
      return found ? found.shortcut : metal.substring(0, 2).toUpperCase();
    };

    const getItemType = (name, item) => {
      if (!name) return '';
      if (name.toLowerCase() === "miscellaneous" && item?.miscitemShortcut) {
        return item.miscitemShortcut;
      }
      const found = itemCategories.find(cat =>
        cat.name.toLowerCase() === name.toLowerCase()
      );
      return found ? found.shortcut : name.substring(0, 3).toUpperCase();
    };

    function wastageToAlphanumeric(wastage) {
      if (!wastage || wastage === "0" || wastage === "0.0") return "";
      const num = parseFloat(wastage);
      if (isNaN(num)) return "";
      if (Number.isInteger(num)) {
        if (num <= 26) {
          return String.fromCharCode(64 + num);
        } else {
          return num.toString();
        }
      }
      const wholePart = Math.floor(num);
      const decimalPart = Math.round((num - wholePart) * 100);
      if (wholePart === 0) {
        return decimalPart.toString();
      }
      if (wholePart <= 26) {
        const letter = String.fromCharCode(64 + wholePart);
        if (decimalPart > 0) {
          return letter + decimalPart.toString();
        }
        return letter;
      } else {
        if (decimalPart > 0) {
          return wholePart.toString() + decimalPart.toString();
        }
        return wholePart.toString();
      }
    }

    const getPurity = (melting) => {
      if (!melting) return '92';
      const purity = Math.round(melting / 100 * 22);
      return purity.toString();
    };

    // Check if item is MRP item
    const isMRPItem = (item) => {
      return (parseFloat(item.weight) === 0 &&
        parseFloat(item.grossWeight) === 0 &&
        parseFloat(item.price) > 0) || item.isMRPItem === true;
    };

    // Generate labels HTML for all selected items
    let allLabelsHtml = '';

    itemsToPrint.forEach((item, index) => {
      const isMRP = isMRPItem(item);

      let formattedData;

      if (isMRP) {
        // MRP Label Data
        formattedData = {
          serialNo: item.serialNo || '0',
          name: item.name || '',
          mrp: parseFloat(item.mrp || item.price || '0').toFixed(2),
          metal: getMetalShort(item.metal),
          metalName: item.metal || '',
          melting: item.melting || '0',
          itemType: getItemType(item.name, item),
          supplierShortcut: item?.supplierShortcut || '',
          description: item.description || item.mrpDescription || '',
          isMRP: true
        };
      } else {
        // Regular Weight-based Label Data
        formattedData = {
          serialNo: item.serialNo || '0',
          name: item.name || '',
          weight: formatNumber(item.weight),
          grossWeight: formatNumber(item.grossWeight),
          lessWeight: isNaN(formatNumber(item.lessWeight)) ? '0.000' : formatNumber(item.lessWeight),
          metal: getMetalShort(item.metal),
          metalName: item.metal || '',
          huid: item.huid || '',
          makingCharges: item.makingCharges || '15',
          wastage: wastageToAlphanumeric(item.wastage) || '0',
          purity: getPurity(item.melting),
          melting: item.melting || '0',
          itemType: getItemType(item.name, item),
          supplierShortcut: item?.supplierShortcut || '',
          description: item.description || '',
          isMRP: false
        };
      }

      // Get QR data
      // const qrData = item.qr || `JWL${Date.now()}-${index + 1}`;
      // const escapedQrData = qrData.replace(/"/g, '\\"').replace(/'/g, "\\'");

      // Build label HTML based on type (same as inventory view)
      let labelContent = '';

      if (isMRP) {
        // MRP Label - EXACT SAME AS INVENTORY VIEW
        labelContent = `
      <div class="label-container">
        <div class="label">
          <div class="left">
            <div class="left-inner">
              <div class="qr-side">
                <div id="pl-qr-${index}" class="qr">
                  <div class="qr-text">Generating QR...</div>
                </div>
              </div>
              <div class="text-side">
                <div class="shop-name">Aurum Silver</div>
              </div>
            </div>
          </div>
          <div class="right">
            <div class="details">
              <span class="item-type">${formattedData.itemType}</span>
              <span class="item-sr-no">${formattedData.serialNo}</span>
              <span class="item-metal"> | ${formattedData.metal}</span>
              <span class="item-purity"> | ${formattedData.melting}</span>
            </div>
            <div class="mrp-price">
              MRP: ₹${formattedData.mrp}
            </div>
            ${formattedData.description ? `<div class="mrp-description">${formattedData.description}</div>` : ''}
          </div>
        </div>
        <div class="blank-space"></div>
      </div>
      `;
      } else {
        // Regular Weight-based Label - EXACT SAME AS INVENTORY VIEW
        labelContent = `
      <div class="label-container">
        <div class="label">
          <div class="left">
            <div class="left-inner">
              <div class="qr-side">
                <div id="pl-qr-${index}" class="qr">
                  <div class="qr-text">Generating QR...</div>
                </div>
              </div>
              <div class="text-side">
                <div class="shop-name">Aurum Silver</div>
                <div class="making">
                  Labour: <span class="making-charges">${formattedData.makingCharges}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="right">
            <div class="details">
              <span class="item-type">${formattedData.itemType}</span>
              <span class="item-sr-no">${formattedData.serialNo}</span>
              <span class="item-supplier"> | ${formattedData.supplierShortcut}</span>
              <span class="item-metal"> | ${formattedData.metal}</span>
              <span class="item-purity"> | ${formattedData.melting}</span>
            </div>
            <div class="weight">
              <span class="gross-weight">GW-<span class="gross-weight-val">${formattedData.grossWeight}</span></span>
              <span class="less-weight">LW-<span class="less-weight-val">${formattedData.lessWeight}</span></span>
            </div>
            <div class="weight">NW - <span class="net-weight">${formattedData.weight}</span></div>
          </div>
        </div>
        <div class="blank-space"></div>
      </div>
      `;
      }

      // Each label is a separate page when printing
      allLabelsHtml += `
    <div class="label-page">
      ${labelContent}
    </div>
    `;
    });

    // Create the complete HTML document with all labels - EXACT SAME STYLES AS INVENTORY VIEW
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Print Labels - ${itemsToPrint.length} Items</title>
  <meta charset="UTF-8">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    @page {
      size: 10cm 1.5cm;
      margin: 0;
      padding: 0;
    }

    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      font-family: Arial, sans-serif;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .label-page {
      width: 10cm;
      height: 1.5cm;
      page-break-after: always;
      page-break-inside: avoid;
    }

    .label-page:last-child {
      page-break-after: avoid;
    }
    
    .label-container {
      width: 10cm;
      height: 1.5cm;
      display: flex;
    }
    
    .blank-space {
      width: 3.5cm;
      height: 1.5cm;
    }
    
    .label {
      width: 6.5cm;
      height: 1.5cm;
      font-family: Arial, sans-serif;
      font-size: 8px;
      display: flex;
      box-sizing: border-box;
    }
    
    .left, .right {
      width: 3.2cm;
      height: 1.5cm;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      box-sizing: border-box;
    }
    
    .left {
      padding: 2px 0 0 5px;
      text-align: left;
    }
    
    .left-inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      height: 100%;
    }
    
    .qr-side {
      flex: 0 0 35%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    
    .text-side {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding-left: 2px;
    }
    
    .qr {
      width: 1.2cm;
      height: 1.2cm;
      background: #f0f0f0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
      font-size: 6px;
      overflow: hidden;
    }

    #qr-canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }
    
    .qr-text {
      font-size: 6px;
      text-align: center;
      line-height: 1;
    }
    
    .shop-name {
      font-size: 11px;
      font-weight: 900;
      color: black;
      text-align: center;
      width: 100%;
    }
    
    .making {
      text-align: center;
      width: 100%;
      font-weight: 700;
      font-size: 10px;
      margin-top: 1px;
    }
    
    .right {
      padding-left: 12px;
      padding-top: 8px;
    }
    
    .details {
      font-size: 10px;
      font-weight: 750;
    }
    
    .weight {
      font-size: 10px;
      font-weight: 750;
    }

    .mrp-price {
      font-size: 12px;
      font-weight: 700;
      margin-top: 2px;
    }

    .mrp-description {
      font-size: 8px;
      color: #64748b;
      margin-top: 1px;
    }
            
    @media print {
      html, body {
        width: 100% !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      body * {
        visibility: visible !important;
      }
      
      .label-page {
        page-break-after: always !important;
        page-break-inside: avoid !important;
        width: 10cm !important;
        height: 1.5cm !important;
      }
      
      .label-page:last-child {
        page-break-after: avoid !important;
      }
    }
  </style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
</head>
<body>
  ${allLabelsHtml}
  
  <script>
    // QR code data for all items
    const itemsData = ${JSON.stringify(itemsToPrint.map(item => ({ qr: item.qr || `JWL${Date.now()}` })))};
    
    function generateAllQRCodes() {
      itemsData.forEach((item, index) => {
        const qrData = item.qr || '';
        const qrContainer = document.getElementById(\`pl-qr-\${index}\`);
        
        if (qrContainer && qrData && qrData.trim() !== '') {
          try {
            qrContainer.innerHTML = '';
            const canvas = document.createElement('canvas');
            canvas.id = \`qr-canvas-\${index}\`;
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            canvas.style.display = 'block';
            qrContainer.appendChild(canvas);
            
            if (typeof QRious !== 'undefined') {
              new QRious({ 
                element: canvas, 
                value: qrData, 
                size: 240, 
                level: 'H', 
                background: 'white', 
                foreground: 'black', 
                backgroundAlpha: 1, 
                foregroundAlpha: 1, 
                padding: 2 
              });
            } else {
              throw new Error('QRious library not loaded');
            }
          } catch (error) {
            console.error('QR generation error for item', index, error);
            qrContainer.innerHTML = '<div class="qr-text" style="font-size: 6px; line-height: 1.2;">QR: ' + qrData.substring(0, 10) + (qrData.length > 10 ? '...' : '') + '</div>';
          }
        } else if (qrContainer) {
          qrContainer.innerHTML = '<div class="qr-text">NO QR</div>';
        }
      });
      
      // Auto-print after QR codes are generated
      setTimeout(() => { 
        window.print(); 
        setTimeout(() => window.close(), 2000); 
      }, 1000);
    }
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', generateAllQRCodes);
    } else {
      generateAllQRCodes();
    }
  </script>
</body>
</html>
  `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();

    // Show success message
    // const mrpCount = itemsToPrint.filter(item => isMRPItem(item)).length;
    // const regularCount = itemsToPrint.length - mrpCount;

    // let labelTypeMessage = '';
    // if (mrpCount > 0 && regularCount > 0) {
    //   labelTypeMessage = ` (${mrpCount} MRP, ${regularCount} Regular)`;
    // } else if (mrpCount > 0) {
    //   labelTypeMessage = ' (MRP Labels)';
    // } else {
    //   labelTypeMessage = ' (Regular Labels)';
    // }

    // setSnackbar({
    //   open: true,
    //   message: `Printing ${itemsToPrint.length} label(s)${labelTypeMessage}...`,
    //   severity: 'success'
    // });
  };

  // ========= MAIN HANDLE SUBMIT =========
  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldValue }) => {
    console.log('=== FORM SUBMIT STARTED ===');
    console.log('Form values on submit:', values);
    console.log('Gross Weight Product mode:', isGrossWeightProduct);
    console.log('MRP Mode:', isMRPMode);

    if (isSubmitting || submittingRef.current) {
      console.log('Submission already in progress - blocking duplicate');
      setSubmitting(false);
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (isGrossWeightProduct) {
        console.log('Handling Gross Weight Product submission');
        await handleGrossWeightSubmit(values, { resetForm, setFieldValue });
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // Handle MRP Mode
      if (isMRPMode) {
        console.log('Handling MRP Mode submission');

        // Check if we have itemWeights with individual MRPs
        const hasIndividualMRPs = itemWeights.some(w => w.mrp && parseFloat(w.mrp) > 0);

        // If no individual MRPs, use the main MRP value
        const defaultMRP = values.mrp || 0;

        if (!defaultMRP && !hasIndividualMRPs) {
          alert('Please enter at least one MRP value');
          setIsSubmitting(false);
          setSubmitting(false);
          submittingRef.current = false;
          return;
        }

        // Prepare itemWeights with MRP values
        const itemsToAdd = itemWeights.map((weight, index) => {
          const mrpValue = weight.mrp ? parseFloat(weight.mrp) : parseFloat(defaultMRP);
          const serialNo = weight.serialNo || getNextSerialNumber(values.name, values.metal);

          return {
            serialNo: serialNo.toString(),
            grossWeight: '0',
            lessWeight: '0',
            netWeight: '0',
            mrp: mrpValue,
            price: mrpValue, // Store MRP in price key
            description: values.mrpDescription || values.description || ''
          };
        });

        // Prepare submission data
        const submissionData = {
          ...values,
          isMRPItem: true,
          sold: 'No',
          qr: `JWL${Date.now()}`,
          melting: values.melting || '0',
          makingCharges: '0',
          silverRate: '0',
          labour: '0',
          goldCuttingRate: '0',
          wastage: '0',
          itemWeights: itemsToAdd,
          // Store multiple MRPs in a separate field for reference
          mrps: itemsToAdd.map(item => item.mrp)
        };

        console.log('MRP Submission data:', submissionData);

        const result = await addBulkStock(submissionData);

        if (result && result.success) {
          const createdItems = result.data?.itemsCreated || [];
          const itemsToShow = createdItems.length > 0 ? createdItems : [{
            ...submissionData,
            id: Date.now().toString()
          }];

          if (itemsToShow.length > 0) {
            const qrImagesMap = {};
            for (const item of itemsToShow) {
              try {
                const qrDataUrl = await QRCode.toDataURL(item.qr || submissionData.qr, {
                  width: 150,
                  margin: 1
                });
                qrImagesMap[item.qr || submissionData.qr] = qrDataUrl;
              } catch (err) {
                console.error('Error generating QR code for item:', err);
              }
            }
            setQrImages(qrImagesMap);
          }

          setLastAddedItem({
            ...submissionData,
            createdItems: itemsToShow,
            isMRPItem: true
          });
          setShowQR(true);

          alert(`✅ MRP Item added successfully!\nItem: ${values.name}\nMRP: ₹${parseFloat(values.mrp).toFixed(2)}\nSerial No: `);

          resetForm();
          setPersistedFormValues({
            supplierName: '',
            supplierShortcut: '',
            name: '',
            metal: '',
            silverType: '',
            melting: '',
            grossWeight: '',
            lessWeight: '',
            weight: '',
            silverRate: '',
            labour: '',
            goldCuttingRate: '',
            wastage: '',
            makingCharges: '',
            serialNo: '',
            description: '',
            huidCodes: [''],
            itemDetail: '',
            miscitemName: '',
            miscitemShortcut: '',
            numberOfPieces: '',
            grossWeightInput: '',
            partyName: '',
            grossDescription: '',
            mrp: '',
            mrpDescription: ''
          });
          setItemWeights([{ serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
        } else {
          throw new Error(result?.message || 'Failed to add MRP item');
        }

        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // Regular mode submission
      console.log('Handling Regular mode submission');

      const hasEmptySerial = itemWeights.some(w => !w.serialNo || w.serialNo.trim() === '');
      if (hasEmptySerial) {
        alert("Please assign serial numbers to all weight entries before submitting.\nClick 'Auto-Assign Serial Numbers' or enter manually.");
        setIsSubmitting(false);
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      const serialNos = itemWeights.map(w => parseInt(w.serialNo));
      const duplicateSerials = serialNos.filter((num, index) => serialNos.indexOf(num) !== index);
      if (duplicateSerials.length > 0) {
        alert(`Duplicate serial numbers found: ${duplicateSerials.join(', ')}. Please fix them before submitting.`);
        setIsSubmitting(false);
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      for (const weight of itemWeights) {
        const serialNo = parseInt(weight.serialNo);
        if (!isNaN(serialNo) && checkForDuplicate(values.name, values.metal, serialNo)) {
          alert(`Serial number ${serialNo} already exists for ${values.name} (${values.metal}). Please use a different serial number.`);
          setIsSubmitting(false);
          setSubmitting(false);
          submittingRef.current = false;
          return;
        }
      }

      const qrCode = `JWL${Date.now()}`;

      const submissionData = {
        ...values,
        huid: values.huidCodes.filter(Boolean).join(', '),
        qr: qrCode,
        sold: 'No',
        itemWeights: itemWeights.map(w => ({
          serialNo: w.serialNo || '',
          grossWeight: w.grossWeight || '0',
          lessWeight: w.lessWeight || '0',
          netWeight: w.netWeight || '0'
        }))
      };

      if (['Gold', 'RoseGold', 'WhiteGold'].includes(values.metal)) {
        submissionData.silverRate = '';
        submissionData.huid = values.huidCodes.filter(Boolean).join(', ');
      } else if (values.metal === 'Silver') {
        submissionData.goldCuttingRate = '';
        submissionData.huid = '';
      } else if (values.metal === 'Gemstone') {
        submissionData.goldCuttingRate = '';
        submissionData.melting = '';
        submissionData.wastage = '';
        submissionData.huid = '';
      }

      delete submissionData.huidCodes;

      if (['Gold', 'RoseGold', 'WhiteGold'].includes(values.metal)) {
        submissionData.silverRate = '';
      } else {
        submissionData.goldCuttingRate = '';
        submissionData.huid = '';
      }

      console.log('Calling addBulkStock API...');
      const result = await addBulkStock(submissionData);
      console.log('API Response:', result);

      if (result && result.success) {
        const createdItems = result.data?.itemsCreated || [];

        if (createdItems.length > 0) {
          const qrImagesMap = {};
          for (const item of createdItems) {
            try {
              const qrDataUrl = await QRCode.toDataURL(item.qr, {
                width: 150,
                margin: 1
              });
              qrImagesMap[item.qr] = qrDataUrl;
            } catch (err) {
              console.error('Error generating QR code for item:', err);
            }
          }
          setQrImages(qrImagesMap);
          setGeneratedQR(createdItems[0].qr);
          if (createdItems[0].qr && qrImagesMap[createdItems[0].qr]) {
            setQrImage(qrImagesMap[createdItems[0].qr]);
          }
        }

        setLastAddedItem({
          ...submissionData,
          createdItems: createdItems,
          itemWeights: submissionData.itemWeights
        });
        setShowQR(true);

        const serialNumbers = itemWeights.map(w => w.serialNo).join(', ');
        alert(`✅ ${itemWeights.length} items added successfully!\nSerial Numbers: ${serialNumbers}`);

        resetForm();
        setItemWeights([{ serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
        setPersistedFormValues({
          supplierName: '',
          supplierShortcut: '',
          name: '',
          metal: '',
          silverType: '',
          melting: '',
          grossWeight: '',
          lessWeight: '',
          weight: '',
          silverRate: '',
          labour: '',
          goldCuttingRate: '',
          wastage: '',
          makingCharges: '',
          serialNo: '',
          description: '',
          huidCodes: [''],
          itemDetail: '',
          miscitemName: '',
          miscitemShortcut: '',
          numberOfPieces: '',
          grossWeightInput: '',
          partyName: '',
          grossDescription: '',
          mrp: '',
          mrpDescription: ''
        });
      } else {
        throw new Error(result?.message || 'Failed to add items');
      }
    } catch (error) {
      console.error('Error adding items:', error);
      alert(error.message || 'Failed to add items. Please try again.');
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
      submittingRef.current = false;
      console.log('=== FORM SUBMIT COMPLETED ===');
    }
  };

  // ========= TOGGLE HANDLERS =========
  const handleGrossWeightToggle = () => {
    setIsGrossWeightProduct(!isGrossWeightProduct);
    if (!isGrossWeightProduct) {
      setIsMRPMode(false);
    }
    const clearedValues = {
      supplierName: '',
      supplierShortcut: '',
      name: '',
      metal: '',
      silverType: '',
      melting: '',
      grossWeight: '',
      lessWeight: '',
      weight: '',
      silverRate: '',
      labour: '',
      goldCuttingRate: '',
      wastage: '',
      makingCharges: '',
      serialNo: '',
      description: '',
      huidCodes: [''],
      itemDetail: '',
      miscitemName: '',
      miscitemShortcut: '',
      numberOfPieces: '',
      grossWeightInput: '',
      partyName: '',
      grossDescription: '',
      mrp: '',
      mrpDescription: ''
    };
    setPersistedFormValues(clearedValues);
    setDynamicFields({});
    setShowQR(false);
    setLastAddedItem(null);
    setSelectedGrossItem(null);
    setIsAddingNewItem(false);
    setNewGrossItemName('');
    setNewGrossItemMetal('');
    setGrossOperation('add');
    setItemWeights([{ serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
  };

  const handleMRPModeToggle = () => {
    setIsMRPMode(!isMRPMode);
    if (!isMRPMode) {
      setIsGrossWeightProduct(false);
    }
    const clearedValues = {
      supplierName: '',
      supplierShortcut: '',
      name: '',
      metal: '',
      silverType: '',
      melting: '',
      grossWeight: '',
      lessWeight: '',
      weight: '',
      silverRate: '',
      labour: '',
      goldCuttingRate: '',
      wastage: '',
      makingCharges: '',
      serialNo: '',
      description: '',
      huidCodes: [''],
      itemDetail: '',
      miscitemName: '',
      miscitemShortcut: '',
      numberOfPieces: '',
      grossWeightInput: '',
      partyName: '',
      grossDescription: '',
      mrp: '',
      mrpDescription: ''
    };
    setPersistedFormValues(clearedValues);
    setDynamicFields({});
    setShowQR(false);
    setLastAddedItem(null);
    setSelectedGrossItem(null);
    setIsAddingNewItem(false);
    setNewGrossItemName('');
    setNewGrossItemMetal('');
    setGrossOperation('add');
    setItemWeights([{ serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
  };

  const handleReset = (resetForm) => {
    const clearedValues = {
      supplierName: '',
      supplierShortcut: '',
      name: '',
      metal: '',
      melting: '',
      grossWeight: '',
      lessWeight: '',
      weight: '',
      silverRate: '',
      goldCuttingRate: '',
      wastage: '',
      makingCharges: '',
      serialNo: '',
      description: '',
      huidCodes: [''],
      itemDetail: '',
      miscitemName: '',
      miscitemShortcut: '',
      numberOfPieces: '',
      grossWeightInput: '',
      partyName: '',
      grossDescription: '',
      mrp: '',
      mrpDescription: ''
    };
    resetForm({ values: clearedValues });
    setPersistedFormValues(clearedValues);
    setDynamicFields({});
    setShowQR(false);
    setLastAddedItem(null);
    setItemWeights([{ serialNo: '', grossWeight: '', lessWeight: '', netWeight: '' }]);
  };

  // ========= STYLED SELECT COMPONENT =========
  const StyledSelect = ({ label, children, ...props }) => {
    return (
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
          {label}
        </InputLabel>
        <Select
          {...props}
          label={label}
          sx={{
            borderRadius: 2,
            backdropFilter: 'blur(10px)',
            width: '250px',
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : '#ffffff',
              isDark ? 0.3 : 0.8
            ),
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(
                isDark ? theme.palette.divider : '#e2e8f0',
                isDark ? 0.2 : 0.5
              ),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(
                isDark ? theme.palette.divider : '#e2e8f0',
                isDark ? 0.3 : 0.7
              ),
            },
          }}
        >
          {children}
        </Select>
      </FormControl>
    );
  };

  if (loading) {
    return (
      <Backdrop
        open={true}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha(isDark ? '#0f172a' : '#ffffff', 0.8),
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={60}
            sx={{
              color: isDark ? '#60a5fa' : '#10b981',
              mb: 2
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: isDark ? '#e2e8f0' : '#2d3748',
              fontWeight: 600
            }}
          >
            Loading...
          </Typography>
        </Box>
      </Backdrop>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: isDark
            ? 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)',
          zIndex: 0,
        }
      }}
    >
      <Backdrop
        open={isSubmitting}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          color: '#fff',
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha(isDark ? '#0f172a' : '#ffffff', 0.8),
        }}
      >
        <Box sx={{
          textAlign: 'center',
          p: 4,
          borderRadius: 3,
          backdropFilter: 'blur(20px)',
          background: alpha(
            isDark ? theme.palette.background.paper : '#ffffff',
            isDark ? 0.3 : 0.8
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          boxShadow: theme.shadows[6],
        }}>
          <CircularProgress
            sx={{
              color: isDark ? '#60a5fa' : '#10b981',
              mb: 2
            }}
          />
          <Typography
            sx={{
              fontWeight: 600,
              color: isDark ? '#e2e8f0' : '#2d3748',
            }}
          >
            {isGrossWeightProduct ? 'Adding Gross Weight Product...' : isMRPMode ? 'Adding MRP Item...' : 'Adding Items...'}
          </Typography>
        </Box>
      </Backdrop>

      {isUpdatingMasterData && (
        <Backdrop
          open={true}
          sx={{
            zIndex: theme.zIndex.drawer + 2,
            backdropFilter: 'blur(4px)',
            backgroundColor: alpha(isDark ? '#0f172a' : '#ffffff', 0.5),
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography sx={{ mt: 2, color: isDark ? '#fff' : '#000' }}>
              Updating master data...
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Card sx={{
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          background: alpha(theme.palette.background.paper, 0.4),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          boxShadow: theme.shadows[4],
        }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <AddIcon sx={{
                    fontSize: 30,
                    color: '#475569',
                  }} />
                </Box>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      background: isDark
                        ? 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)'
                        : 'linear-gradient(90deg, #059669 0%, #10b981 100%)',
                      backgroundClip: 'text',
                      letterSpacing: '-0.02em',
                      fontSize: { xs: '1rem', md: '1.5rem' },
                      color: isDark ? "#f1f5f9" : "#1e293b",
                    }}
                  >
                    Add New Jewellery Item
                  </Typography>
                  <Typography
                    sx={{
                      color: isDark ? '#cbd5e1' : '#64748b',
                      fontSize: '0.9rem',
                      fontWeight: 400,
                    }}
                  >
                    Enter item details to add to inventory
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Management Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={() => {
              setEditingItem(null);
              setNewItemName('');
              setNewItemShortcut('');
              setItemDialogOpen(true);
            }}
            disabled={isUpdatingMasterData}
            sx={{
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          >
            Manage Items ({itemCategories.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<DiamondIcon />}
            onClick={() => {
              setEditingMetal(null);
              setNewMetalName('');
              setNewMetalShortcut('');
              setMetalDialogOpen(true);
            }}
            disabled={isUpdatingMasterData}
            sx={{
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.background.paper, 0.3),
              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            }}
          >
            Manage Metals ({metalTypes.length})
          </Button>
        </Box>

        {/* Mode Toggles Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {/* Gross Weight Product Toggle */}
          {isSecretMode && (
            <GrossWeightToggle isDark={isDark} elevation={0} sx={{ borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isGrossWeightProduct}
                    onChange={handleGrossWeightToggle}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#8B5CF6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#8B5CF6',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{
                    fontWeight: 600,
                    color: isDark ? '#e2e8f0' : '#475569',
                    fontSize: '1.1rem'
                  }}>
                    {isGrossWeightProduct ? '💎 Gross Weight Product Mode (Minimum Details)' : '📋 Regular Product Mode (All Details)'}
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                display: 'block',
                mt: 1
              }}>
                {isGrossWeightProduct
                  ? 'Add or subtract stock from existing items, or create new items with initial stock.'
                  : 'Regular mode requires all item details for proper inventory tracking.'}
              </Typography>
            </GrossWeightToggle>
          )}

          {/* MRP Mode Toggle */}
          {!isGrossWeightProduct && (
            <Card sx={{
              borderRadius: 2,
              overflow: 'hidden',
              backdropFilter: 'blur(20px)',
              background: alpha(theme.palette.background.paper, 0.4),
              border: `1px solid ${alpha(
                isDark ? theme.palette.divider : '#e2e8f0',
                isDark ? 0.2 : 0.3
              )}`,
              boxShadow: theme.shadows[4],
            }}>
              <CardContent sx={{ p: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isMRPMode}
                      onChange={handleMRPModeToggle}
                      color="success"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#059669',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#059669',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{
                      fontWeight: 600,
                      color: isDark ? '#e2e8f0' : '#475569',
                      fontSize: '1.1rem'
                    }}>
                      {isMRPMode ? '🏷️ MRP Mode (Direct Pricing)' : '📦 Standard Mode (Weight Based)'}
                    </Typography>
                  }
                />
                <Typography variant="caption" sx={{
                  color: isDark ? '#94a3b8' : '#64748b',
                  display: 'block',
                  mt: 1
                }}>
                  {isMRPMode
                    ? 'Add items with MRP. Weight fields will be set to 0.'
                    : 'Standard mode requires weight details and all other specifications.'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={8} sx={{ margin: "auto" }}>
            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema(isGrossWeightProduct, isMRPMode)}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ values, errors, touched, setFieldValue, isSubmitting: formikSubmitting, resetForm, submitForm }) => {
                return (
                  <Form>
                    {/* Show Gross Weight Product Form */}
                    {isGrossWeightProduct ? (
                      // ... Gross Weight Product Form (same as before)
                      <>
                        <Card sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          backdropFilter: 'blur(20px)',
                          background: alpha(theme.palette.background.paper, 0.5),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: theme.shadows[4],
                          mb: 3,
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{
                              mb: 3,
                              fontWeight: 700,
                              color: isDark ? '#e2e8f0' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <ScaleIcon fontSize="small" />
                              Gross Weight Product Management
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                              <Button
                                variant={!selectedGrossItem && !isAddingNewItem ? 'contained' : 'outlined'}
                                onClick={() => {
                                  setSelectedGrossItem(null);
                                  setIsAddingNewItem(false);
                                }}
                                sx={{ borderRadius: 2 }}
                              >
                                Select Existing Item
                              </Button>
                              <Button
                                variant={isAddingNewItem ? 'contained' : 'outlined'}
                                onClick={() => {
                                  setIsAddingNewItem(true);
                                  setSelectedGrossItem(null);
                                }}
                                sx={{ borderRadius: 2 }}
                              >
                                + Create New Item
                              </Button>
                            </Box>

                            {!selectedGrossItem && !isAddingNewItem && (
                              <Box sx={{ mb: 3 }}>
                                <Autocomplete
                                  options={grossStockItems}
                                  getOptionLabel={(option) =>
                                    `${option.itemName} (${option.metal}) - ${option.currentStock} pcs, ${option.currentWeight}g`
                                  }
                                  onChange={(event, newValue) => {
                                    setSelectedGrossItem(newValue);
                                    if (newValue) {
                                      setIsGrossItemBased(newValue.isPieceBased);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Search and Select Item"
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                      sx={{ borderRadius: 2 }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props} style={{ padding: '8px 16px' }}>
                                      <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                          {option.itemName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                          Metal: {option.metal} | Stock: {option.currentStock} pcs, {option.currentWeight}g
                                          {option.isPieceBased ? ' | Piece-Based' : ' | Weight-Based'}
                                        </Typography>
                                      </Box>
                                    </li>
                                  )}
                                />
                                {grossStockItems.length === 0 && (
                                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 1, display: 'block' }}>
                                    No items found. Click "Create New Item" to add one.
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {isAddingNewItem && (
                              <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Item Name *"
                                    value={newGrossItemName}
                                    onChange={(e) => setNewGrossItemName(e.target.value)}
                                    placeholder="e.g., Notebook, Book, Silver Bar"
                                    size="small"
                                    required
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Metal/Category"
                                    value={newGrossItemMetal}
                                    onChange={(e) => setNewGrossItemMetal(e.target.value)}
                                    placeholder="e.g., Silver, Gold, Paper"
                                    size="small"
                                  />
                                </Grid>
                              </Grid>
                            )}

                            {selectedGrossItem && (
                              <Paper sx={{
                                p: 2,
                                mb: 3,
                                borderRadius: 2,
                                background: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  Selected: {selectedGrossItem.itemName}
                                </Typography>
                                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  Metal: {selectedGrossItem.metal} |
                                  Current Stock: {selectedGrossItem.currentStock} pcs, {selectedGrossItem.currentWeight}g |
                                  {selectedGrossItem.isPieceBased ? ' Piece-Based' : ' Weight-Based'}
                                </Typography>
                              </Paper>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                              <Button
                                variant={grossOperation === 'add' ? 'contained' : 'outlined'}
                                color="success"
                                onClick={() => setGrossOperation('add')}
                                sx={{ borderRadius: 2 }}
                              >
                                ➕ Add Stock
                              </Button>
                              <Button
                                variant={grossOperation === 'subtract' ? 'contained' : 'outlined'}
                                color="error"
                                onClick={() => setGrossOperation('subtract')}
                                sx={{ borderRadius: 2 }}
                              >
                                ➖ Subtract Stock
                              </Button>
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label="Number of Pieces"
                                  name="numberOfPieces"
                                  value={values.numberOfPieces || ''}
                                  onChange={(e) => setFieldValue('numberOfPieces', e.target.value)}
                                  placeholder="Enter number of pieces"
                                  size="small"
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">pcs</InputAdornment>,
                                  }}
                                  helperText="Enter the number of pieces to add/subtract"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label="Weight (g)"
                                  name="grossWeightInput"
                                  value={values.grossWeightInput || ''}
                                  onChange={(e) => setFieldValue('grossWeightInput', e.target.value)}
                                  placeholder="Enter weight in grams"
                                  size="small"
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                  }}
                                  helperText="Enter the weight in grams to add/subtract"
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  label="Party Name (Optional)"
                                  name="partyName"
                                  value={values.partyName || ''}
                                  onChange={(e) => setFieldValue('partyName', e.target.value)}
                                  placeholder="Customer/Supplier name"
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Description (Optional)"
                                  name="grossDescription"
                                  value={values.grossDescription || ''}
                                  onChange={(e) => setFieldValue('grossDescription', e.target.value)}
                                  placeholder="Add a note about this transaction"
                                  size="small"
                                  multiline
                                  rows={2}
                                />
                              </Grid>
                            </Grid>

                            <Typography variant="caption" sx={{
                              display: 'block',
                              mt: 2,
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontStyle: 'italic'
                            }}>
                              {selectedGrossItem
                                ? `${grossOperation === 'add' ? 'Adding' : 'Subtracting'} to/from "${selectedGrossItem.itemName}"`
                                : isAddingNewItem
                                  ? 'Creating new item with initial stock'
                                  : 'Select an existing item or create a new one'}
                            </Typography>
                          </CardContent>
                        </Card>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <Button
                            type="button"
                            variant="contained"
                            disabled={isSubmitting || formikSubmitting || isUpdatingMasterData}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
                            onClick={submitForm}
                            sx={{
                              borderRadius: 3,
                              fontWeight: 600,
                              textTransform: 'none',
                              background: isDark
                                ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
                                : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                              boxShadow: isDark
                                ? '0 4px 14px 0 rgba(139, 92, 246, 0.35)'
                                : '0 4px 14px 0 rgba(139, 92, 246, 0.35)',
                              '&:hover': {
                                background: isDark
                                  ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
                                  : 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            {isSubmitting ? 'Processing...' : selectedGrossItem ? `Update ${selectedGrossItem.itemName}` : isAddingNewItem ? 'Create & Add Item' : 'Select or Create Item'}
                          </Button>
                          <Button
                            type="button"
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                              setSelectedGrossItem(null);
                              setIsAddingNewItem(false);
                              setNewGrossItemName('');
                              setNewGrossItemMetal('');
                              setGrossOperation('add');
                              resetForm();
                            }}
                            sx={{
                              borderRadius: 3,
                              fontWeight: 600,
                              textTransform: 'none',
                              backdropFilter: 'blur(10px)',
                              background: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                              color: isDark ? '#e2e8f0' : '#475569',
                              border: `1.5px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.5
                              )}`,
                              px: 4,
                              py: 1.5,
                              '&:hover': {
                                background: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.5 : 0.9
                                ),
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            Reset
                          </Button>
                        </Box>
                      </>
                    ) : (
                      // ========= REGULAR MODE & MRP MODE FORM =========
                      <>
                        {/* Supplier Information */}
                        <Card sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          backdropFilter: 'blur(20px)',
                          background: alpha(theme.palette.background.paper, 0.5),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: theme.shadows[4],
                          mb: 3,
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{
                              mb: 3,
                              fontWeight: 700,
                              color: isDark ? '#e2e8f0' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <PersonIcon fontSize="small" />
                              Supplier Information
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Autocomplete
                                  freeSolo
                                  options={supplierOptions}
                                  loading={loadingSuppliers}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return toCapitalCase(option);
                                    }
                                    return toCapitalCase(option.name) || '';
                                  }}
                                  value={values.supplierName || null}
                                  onChange={(event, newValue) => {
                                    if (typeof newValue === 'string') {
                                      const formattedValue = toCapitalCase(newValue);
                                      setFieldValue('supplierName', formattedValue);
                                      setFieldValue('supplierShortcut', '');
                                    } else if (newValue && newValue.name) {
                                      const formattedValue = toCapitalCase(newValue.name);
                                      setFieldValue('supplierName', formattedValue);
                                      if (newValue.shortcut) {
                                        setFieldValue('supplierShortcut', newValue.shortcut.toUpperCase());
                                      }
                                    } else {
                                      setFieldValue('supplierName', '');
                                      setFieldValue('supplierShortcut', '');
                                    }
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    setFieldValue('supplierName', toCapitalCase(newInputValue));
                                  }}
                                  renderInput={(params) => (
                                    <StyledTextField
                                      {...params}
                                      fullWidth
                                      label="Supplier Name *"
                                      name="supplierName"
                                      value={values.supplierName}
                                      error={touched.supplierName && Boolean(errors.supplierName)}
                                      helperText={touched.supplierName && errors.supplierName}
                                      sx={{ width: "250px" }}
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <PersonIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => (
                                    <li {...props} style={{ padding: '8px 16px' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{
                                          bgcolor: alpha('#8B5CF6', 0.1),
                                          color: '#8B5CF6',
                                          fontWeight: 600,
                                          border: `1.5px solid ${alpha('#8B5CF6', 0.3)}`,
                                        }}>
                                          {option.name.charAt(0)}
                                        </Avatar>
                                        <Box>
                                          <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                                            {toCapitalCase(option.name)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                                            {option.shortcut && `Shortcut: ${option.shortcut}`}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </li>
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label="Supplier Shortcut"
                                  name="supplierShortcut"
                                  value={values.supplierShortcut}
                                  onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    setFieldValue('supplierShortcut', value);
                                  }}
                                  error={touched.supplierShortcut && Boolean(errors.supplierShortcut)}
                                  helperText={touched.supplierShortcut && errors.supplierShortcut}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <ShortTextIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Item Details Card */}
                        <Card sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          backdropFilter: 'blur(20px)',
                          background: alpha(theme.palette.background.paper, 0.5),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: theme.shadows[4],
                          mb: 3,
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{
                              mb: 3,
                              fontWeight: 700,
                              color: isDark ? '#e2e8f0' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}>
                              <CategoryIcon fontSize="small" />
                              Item Details
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                  <InputLabel>Item Name *</InputLabel>
                                  <Select
                                    value={values.name}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFieldValue('name', value);
                                      updateDynamicFields(value);
                                      if (!isMRPMode) {
                                        handleItemChange(value, values.metal, setFieldValue);
                                      } else {
                                        // For MRP mode, still get serial number
                                        const nextSerial = getNextSerialNumber(value, values.metal);
                                        setFieldValue('serialNo', nextSerial);
                                      }
                                    }}
                                    label="Item Name *"
                                    error={touched.name && Boolean(errors.name)}
                                    sx={{
                                      borderRadius: 2,
                                      backdropFilter: 'blur(10px)',
                                      width: '250px',
                                    }}
                                  >
                                    <MenuItem value="">Select Item</MenuItem>
                                    {itemCategories.map((item) => (
                                      <MenuItem key={item.id} value={item.name}>
                                        {item.name} ({item.shortcut})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                  <InputLabel>Metal Type *</InputLabel>
                                  <Select
                                    value={values.metal}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setFieldValue('metal', value);
                                      if (!isMRPMode) {
                                        handleItemChange(values.name, value, setFieldValue);
                                      } else {
                                        // For MRP mode, still get serial number
                                        const nextSerial = getNextSerialNumber(values.name, value);
                                        setFieldValue('serialNo', nextSerial);
                                      }
                                    }}
                                    label="Metal Type *"
                                    error={touched.metal && Boolean(errors.metal)}
                                    sx={{
                                      borderRadius: 2,
                                      backdropFilter: 'blur(10px)',
                                      width: '250px',
                                    }}
                                  >
                                    <MenuItem value="">Select Metal</MenuItem>
                                    {metalTypes.map((metal) => (
                                      <MenuItem key={metal.id} value={metal.name}>
                                        {metal.name} ({metal.shortcut})
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>

                            {values.name === 'Miscellaneous' && !isMRPMode && (
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Item Name"
                                    name="miscitemName"
                                    value={values.miscitemName}
                                    onChange={(e) => {
                                      const value = toCapitalCase(e.target.value);
                                      setFieldValue('miscitemName', value);
                                    }}
                                    error={touched.miscitemName && Boolean(errors.miscitemName)}
                                    helperText={touched.miscitemName && errors.miscitemName}
                                    placeholder="Item Name"
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <ShortTextIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Item Shortcut"
                                    name="miscitemShortcut"
                                    value={values.miscitemShortcut}
                                    onChange={(e) => {
                                      const value = toUpperCase(e.target.value);
                                      setFieldValue('miscitemShortcut', value.slice(0, 3));
                                    }}
                                    error={touched.miscitemShortcut && Boolean(errors.miscitemShortcut)}
                                    helperText={touched.miscitemShortcut && errors.miscitemShortcut}
                                    placeholder="Item Shortcut"
                                    inputProps={{ maxLength: 3 }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <ShortTextIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            )}

                            {dynamicFields.type && !isMRPMode && (
                              <Grid item xs={12}>
                                {dynamicFields.type === 'select' ? (
                                  <StyledSelect
                                    label={dynamicFields.label}
                                    name={dynamicFields.name}
                                    value={values[dynamicFields.name] || ''}
                                    onChange={(e) => setFieldValue(dynamicFields.name, e.target.value)}
                                  >
                                    <MenuItem value="">Select {dynamicFields.label}</MenuItem>
                                    {dynamicFields.options.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                ) : (
                                  <StyledTextField
                                    fullWidth
                                    label={dynamicFields.label}
                                    name={dynamicFields.name}
                                    value={values[dynamicFields.name] || ''}
                                    onChange={(e) => setFieldValue(dynamicFields.name, e.target.value)}
                                    placeholder={dynamicFields.placeholder}
                                    type="text"
                                  />
                                )}
                              </Grid>
                            )}
                          </CardContent>
                        </Card>

                        {/* Melting Field - Only for non-MRP mode */}
                        {!isMRPMode && values.metal !== 'Gemstone' && (
                          <Grid item xs={12} md={6}>
                            <StyledTextField
                              fullWidth
                              label="Melting (%)"
                              name="melting"
                              type="text"
                              value={values.melting}
                              onChange={(e) => setFieldValue('melting', e.target.value)}
                              error={touched.melting && Boolean(errors.melting)}
                              helperText={touched.melting && errors.melting}
                              sx={{ mb: 2 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          </Grid>
                        )}

                        {/* MRP Field - Only in MRP mode */}
                        {isMRPMode && (
                          <Card sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            backdropFilter: 'blur(20px)',
                            background: alpha(theme.palette.background.paper, 0.5),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}>
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h5" sx={{
                                  fontWeight: 700,
                                  color: isDark ? '#e2e8f0' : '#475569',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}>
                                  <PriceCheckIcon fontSize="small" />
                                  MRP Items Details
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddWeightRow}
                                    size="small"
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      backdropFilter: 'blur(10px)',
                                      background: alpha(
                                        isDark ? theme.palette.background.paper : '#ffffff',
                                        isDark ? 0.3 : 0.8
                                      ),
                                      border: `1px solid ${alpha(
                                        isDark ? theme.palette.divider : '#e2e8f0',
                                        isDark ? 0.2 : 0.3
                                      )}`,
                                      color: isDark ? '#e2e8f0' : '#475569',
                                    }}
                                  >
                                    Add Another MRP Item
                                  </Button>
                                </Box>
                              </Box>

                              {/* Auto-Assign Serial Numbers Button */}
                              {values.name && values.metal && (
                                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      const assignedWeights = assignSerialNumbers(values.name, values.metal, itemWeights);
                                      setItemWeights(assignedWeights);
                                      if (assignedWeights.length === 1) {
                                        setFieldValue('serialNo', assignedWeights[0].serialNo);
                                      }
                                      alert(`Serial numbers assigned: ${assignedWeights.map(w => w.serialNo).join(', ')}`);
                                    }}
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      borderColor: alpha('#8B5CF6', 0.5),
                                      color: '#8B5CF6',
                                      '&:hover': {
                                        borderColor: '#8B5CF6',
                                        backgroundColor: alpha('#8B5CF6', 0.05),
                                      }
                                    }}
                                  >
                                    Auto-Assign Serial Numbers
                                  </Button>
                                </Box>
                              )}

                              {itemWeights.map((weight, index) => (
                                <Paper
                                  key={index}
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    backdropFilter: 'blur(10px)',
                                    background: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.2 : 0.5
                                    ),
                                    border: `1px solid ${alpha(
                                      isDark ? theme.palette.divider : '#e2e8f0',
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    position: 'relative',
                                  }}
                                >
                                  {itemWeights.length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveWeightRow(index)}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: theme.palette.error.main,
                                        '&:hover': {
                                          background: alpha(theme.palette.error.main, 0.1),
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}

                                  <Typography variant="subtitle2" sx={{
                                    mb: 2,
                                    color: isDark ? '#94a3b8' : '#64748b',
                                    fontWeight: 600
                                  }}>
                                    Item #{index + 1}
                                  </Typography>

                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                      <TextField
                                        fullWidth
                                        label={`Serial No ${index + 1}`}
                                        type="text"
                                        value={weight.serialNo || ''}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '' || /^\d+$/.test(val)) {
                                            handleWeightFieldChange(index, 'serialNo', val, setFieldValue);
                                          }
                                        }}
                                        size="small"
                                        InputProps={{
                                          sx: {
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#f8fafc',
                                              isDark ? 0.2 : 0.5
                                            ),
                                          }
                                        }}
                                        helperText={index === 0 ? "Auto-filled, can be edited" : ""}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        label={`MRP ${index + 1} (₹)`}
                                        type="text"
                                        value={weight.mrp || ''}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                            handleWeightFieldChange(index, 'mrp', val, setFieldValue);
                                          }
                                        }}
                                        InputProps={{
                                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                          sx: {
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#f8fafc',
                                              isDark ? 0.2 : 0.5
                                            ),
                                          }
                                        }}
                                        size="small"
                                        helperText="Enter MRP for this item"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        fullWidth
                                        label={`Description ${index + 1}`}
                                        value={weight.description || ''}
                                        onChange={(e) => handleWeightFieldChange(index, 'description', e.target.value, setFieldValue)}
                                        size="small"
                                        placeholder="Optional description"
                                        InputProps={{
                                          sx: {
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#f8fafc',
                                              isDark ? 0.2 : 0.5
                                            ),
                                          }
                                        }}
                                      />
                                    </Grid>
                                  </Grid>
                                </Paper>
                              ))}

                              {/* Melting Field - Added here */}
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{
                                  mb: 2,
                                  color: isDark ? '#e2e8f0' : '#475569',
                                  fontWeight: 600
                                }}>
                                  Additional Details
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <StyledTextField
                                      fullWidth
                                      label="Melting (%) *"
                                      name="melting"
                                      type="text"
                                      value={values.melting}
                                      onChange={(e) => setFieldValue('melting', e.target.value)}
                                      error={touched.melting && Boolean(errors.melting)}
                                      helperText={touched.melting && errors.melting}
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <StyledTextField
                                      fullWidth
                                      label="Default MRP (₹)"
                                      name="mrp"
                                      type="text"
                                      value={values.mrp || ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                          setFieldValue('mrp', val);
                                        }
                                      }}
                                      error={touched.mrp && Boolean(errors.mrp)}
                                      helperText={(touched.mrp && errors.mrp) || "Used if individual MRPs not provided"}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Common Description (Optional)"
                                      name="mrpDescription"
                                      value={values.mrpDescription || ''}
                                      onChange={(e) => setFieldValue('mrpDescription', e.target.value)}
                                      multiline
                                      rows={2}
                                      size="small"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            <DescriptionIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>

                              {/* Validation Summary */}
                              <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#475569' }}>
                                  Summary:
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" sx={{
                                    color: itemWeights.some(w => w.mrp && parseFloat(w.mrp) > 0) || (values.mrp && parseFloat(values.mrp) > 0)
                                      ? 'success.main'
                                      : 'error.main'
                                  }}>
                                    {itemWeights.some(w => w.mrp && parseFloat(w.mrp) > 0) || (values.mrp && parseFloat(values.mrp) > 0)
                                      ? '✅ MRP values provided'
                                      : '⚠️ Please enter at least one MRP value'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: itemWeights.every(w => w.serialNo && w.serialNo.trim() !== '')
                                      ? 'success.main'
                                      : 'error.main'
                                  }}>
                                    {itemWeights.every(w => w.serialNo && w.serialNo.trim() !== '')
                                      ? '✅ All serial numbers assigned'
                                      : '⚠️ Some items missing serial numbers'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: values.name ? 'success.main' : 'error.main'
                                  }}>
                                    {values.name ? '✅ Item name selected' : '⚠️ Item name required'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: values.metal ? 'success.main' : 'error.main'
                                  }}>
                                    {values.metal ? '✅ Metal type selected' : '⚠️ Metal type required'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: values.supplierName ? 'success.main' : 'error.main'
                                  }}>
                                    {values.supplierName ? '✅ Supplier name entered' : '⚠️ Supplier name required'}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: values.melting ? 'success.main' : 'error.main'
                                  }}>
                                    {values.melting ? '✅ Melting percentage entered' : '⚠️ Melting percentage required'}
                                  </Typography>
                                </Stack>
                              </Box>
                            </CardContent>
                          </Card>
                        )}

                        {/* Weight Details - Only for non-MRP mode */}
                        {!isMRPMode && (
                          <Card sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            backdropFilter: 'blur(20px)',
                            background: alpha(theme.palette.background.paper, 0.5),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                            boxShadow: theme.shadows[4],
                            mb: 3,
                          }}>
                            <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h5" sx={{
                                  fontWeight: 700,
                                  color: isDark ? '#e2e8f0' : '#475569',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}>
                                  <ScaleIcon fontSize="small" />
                                  {values.metal === 'Gemstone' ? 'Weight Details (Carats/Cents)' : 'Weight Details'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddWeightRow}
                                    size="small"
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      backdropFilter: 'blur(10px)',
                                      background: alpha(
                                        isDark ? theme.palette.background.paper : '#ffffff',
                                        isDark ? 0.3 : 0.8
                                      ),
                                      border: `1px solid ${alpha(
                                        isDark ? theme.palette.divider : '#e2e8f0',
                                        isDark ? 0.2 : 0.3
                                      )}`,
                                      color: isDark ? '#e2e8f0' : '#475569',
                                    }}
                                  >
                                    Add Weight Row
                                  </Button>
                                </Box>
                              </Box>

                              {itemWeights.map((weight, index) => (
                                <Paper
                                  key={index}
                                  sx={{
                                    p: 2,
                                    mb: 2,
                                    borderRadius: 2,
                                    backdropFilter: 'blur(10px)',
                                    background: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.2 : 0.5
                                    ),
                                    border: `1px solid ${alpha(
                                      isDark ? theme.palette.divider : '#e2e8f0',
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    position: 'relative',
                                  }}
                                >
                                  {itemWeights.length > 1 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveWeightRow(index)}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: theme.palette.error.main,
                                        '&:hover': {
                                          background: alpha(theme.palette.error.main, 0.1),
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}

                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={2}>
                                      <TextField
                                        fullWidth
                                        label={`Serial No ${index + 1}`}
                                        type="text"
                                        value={weight.serialNo || ''}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === '' || /^\d+$/.test(val)) {
                                            handleWeightFieldChange(index, 'serialNo', val, setFieldValue);
                                          }
                                        }}
                                        size="small"
                                        InputProps={{
                                          sx: {
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#f8fafc',
                                              isDark ? 0.2 : 0.5
                                            ),
                                          }
                                        }}
                                        helperText={index === 0 ? "Auto-filled, can be edited" : ""}
                                      />
                                    </Grid>

                                    {values.metal !== 'Gemstone' && (
                                      <Grid item xs={12} md={3}>
                                        <TextField
                                          fullWidth
                                          label={`Gross Weight ${index + 1} (g)`}
                                          type="text"
                                          value={weight.grossWeight}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '' || val === '.' || /^\d*\.?\d*$/.test(val)) {
                                              handleWeightFieldChange(index, 'grossWeight', val, setFieldValue);
                                            }
                                          }}
                                          onBlur={() => {
                                            const val = weight.grossWeight;
                                            if (val && val !== '.' && !isNaN(parseFloat(val))) {
                                              const formatted = parseFloat(val).toFixed(3);
                                              handleWeightFieldChange(index, 'grossWeight', formatted, setFieldValue);
                                            }
                                          }}
                                          InputProps={{
                                            endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                          }}
                                          size="small"
                                        />
                                      </Grid>
                                    )}
                                    {values.metal !== 'Gemstone' && (
                                      <Grid item xs={12} md={3}>
                                        <TextField
                                          fullWidth
                                          label={`Less Weight ${index + 1} (g)`}
                                          type="text"
                                          value={weight.lessWeight}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '' || val === '.' || /^\d*\.?\d*$/.test(val)) {
                                              handleWeightFieldChange(index, 'lessWeight', val, setFieldValue);
                                            }
                                          }}
                                          onBlur={() => {
                                            const val = weight.lessWeight;
                                            if (val && val !== '.' && !isNaN(parseFloat(val))) {
                                              const formatted = parseFloat(val).toFixed(3);
                                              handleWeightFieldChange(index, 'lessWeight', formatted, setFieldValue);
                                            }
                                          }}
                                          InputProps={{
                                            endAdornment: <InputAdornment position="end">g</InputAdornment>,
                                          }}
                                          size="small"
                                        />
                                      </Grid>
                                    )}
                                    <Grid item xs={12} md={values.metal === 'Gemstone' ? 4 : 2}>
                                      <TextField
                                        fullWidth
                                        label={`Net Weight ${index + 1}`}
                                        type="text"
                                        value={weight.netWeight}
                                        readOnly
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: <InputAdornment position="end">
                                            {values.metal === 'Gemstone' ? 'ct' : 'g'}
                                          </InputAdornment>,
                                          sx: {
                                            backgroundColor: alpha(
                                              isDark ? theme.palette.background.paper : '#f8fafc',
                                              isDark ? 0.2 : 0.5
                                            ),
                                          }
                                        }}
                                        size="small"
                                      />
                                    </Grid>
                                    {values.metal === 'Gemstone' && (
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                          {weight.netWeight ? formatGemstoneWeight(weight.netWeight) : 'Enter weight to see formatted value'}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Paper>
                              ))}

                              {values.name && values.metal && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      const assignedWeights = assignSerialNumbers(values.name, values.metal, itemWeights);
                                      setItemWeights(assignedWeights);
                                      if (assignedWeights.length === 1) {
                                        setFieldValue('serialNo', assignedWeights[0].serialNo);
                                      }
                                      alert(`Serial numbers assigned: ${assignedWeights.map(w => w.serialNo).join(', ')}`);
                                    }}
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                  >
                                    Auto-Assign Serial Numbers
                                  </Button>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        )}

                        {/* Gold/Silver Specific Fields - Hidden in MRP Mode */}
                        {!isMRPMode && (
                          <>
                            {['Gold', 'RoseGold', 'WhiteGold'].includes(values.metal) ? (
                              <Card sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                backdropFilter: 'blur(20px)',
                                background: alpha(theme.palette.background.paper, 0.5),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: theme.shadows[4],
                                mb: 3,
                              }}>
                                <CardContent sx={{ p: 3 }}>
                                  <Typography variant="h5" sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    color: isDark ? '#e2e8f0' : '#475569',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}>
                                    <DiamondIcon fontSize="small" />
                                    Gold Item Details
                                  </Typography>

                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                      <StyledTextField
                                        fullWidth
                                        label="Gold Cutting Rate (₹)"
                                        name="goldCuttingRate"
                                        type="text"
                                        value={values.goldCuttingRate}
                                        onChange={(e) => setFieldValue('goldCuttingRate', e.target.value)}
                                        error={touched.goldCuttingRate && Boolean(errors.goldCuttingRate)}
                                        helperText={touched.goldCuttingRate && errors.goldCuttingRate}
                                      />
                                    </Grid>
                                  </Grid>

                                  <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle1" sx={{
                                      mb: 2,
                                      fontWeight: 600,
                                      color: isDark ? '#e2e8f0' : '#475569',
                                    }}>
                                      HUID Codes
                                    </Typography>
                                    <FieldArray name="huidCodes">
                                      {({ push, remove, form }) => (
                                        <Box>
                                          {form.values.huidCodes.map((_, index) => (
                                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                              <StyledTextField
                                                fullWidth
                                                label={`HUID Code ${index + 1}`}
                                                name={`huidCodes[${index}]`}
                                                value={form.values.huidCodes[index]}
                                                onChange={(e) => {
                                                  const value = toUpperCase(e.target.value);
                                                  setFieldValue(`huidCodes[${index}]`, value);
                                                }}
                                              />
                                              {index > 0 && (
                                                <IconButton
                                                  onClick={() => remove(index)}
                                                  sx={{
                                                    backdropFilter: 'blur(10px)',
                                                    background: alpha(theme.palette.error.main, 0.1),
                                                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                                    color: theme.palette.error.main,
                                                    '&:hover': {
                                                      background: alpha(theme.palette.error.main, 0.2),
                                                    }
                                                  }}
                                                >
                                                  <DeleteIcon />
                                                </IconButton>
                                              )}
                                            </Box>
                                          ))}
                                          <Button
                                            startIcon={<AddIcon />}
                                            onClick={() => push('')}
                                            sx={{
                                              borderRadius: 2,
                                              backdropFilter: 'blur(10px)',
                                              background: alpha(
                                                isDark ? theme.palette.background.paper : '#ffffff',
                                                isDark ? 0.3 : 0.8
                                              ),
                                              border: `1px solid ${alpha(
                                                isDark ? theme.palette.divider : '#e2e8f0',
                                                isDark ? 0.2 : 0.3
                                              )}`,
                                              color: isDark ? '#e2e8f0' : '#475569',
                                            }}
                                          >
                                            Add HUID Code
                                          </Button>
                                        </Box>
                                      )}
                                    </FieldArray>
                                  </Box>
                                </CardContent>
                              </Card>
                            ) : values.metal === 'Gemstone' ? (
                              <Card sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                backdropFilter: 'blur(20px)',
                                background: alpha(theme.palette.background.paper, 0.5),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: theme.shadows[4],
                                mb: 3,
                              }}>
                                <CardContent sx={{ p: 3 }}>
                                  <Typography variant="h5" sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    color: isDark ? '#e2e8f0' : '#475569',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}>
                                    <AttachMoneyIcon fontSize="small" />
                                    Gemstone Details
                                  </Typography>

                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <StyledTextField
                                        fullWidth
                                        label="Rate per Carat (₹)"
                                        name="silverRate"
                                        type="text"
                                        value={values.silverRate}
                                        onChange={(e) => setFieldValue('silverRate', e.target.value)}
                                        error={touched.silverRate && Boolean(errors.silverRate)}
                                        helperText={touched.silverRate && errors.silverRate}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">₹</InputAdornment>
                                          ),
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <StyledTextField
                                        fullWidth
                                        label="Labour (₹)"
                                        name="labour"
                                        type="text"
                                        value={values.labour}
                                        onChange={(e) => setFieldValue('labour', e.target.value)}
                                        error={touched.labour && Boolean(errors.labour)}
                                        helperText={touched.labour && errors.labour}
                                      />
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            ) : (
                              <Card sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                backdropFilter: 'blur(20px)',
                                background: alpha(theme.palette.background.paper, 0.5),
                                border: `1px solid ${alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: theme.shadows[4],
                                mb: 3,
                              }}>
                                <CardContent sx={{ p: 3 }}>
                                  <Typography variant="h5" sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    color: isDark ? '#e2e8f0' : '#475569',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}>
                                    <AttachMoneyIcon fontSize="small" />
                                    Other Metal Details
                                  </Typography>

                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <StyledTextField
                                        fullWidth
                                        label="Silver Rate (₹)"
                                        name="silverRate"
                                        type="text"
                                        value={values.silverRate}
                                        onChange={(e) => setFieldValue('silverRate', e.target.value)}
                                        error={touched.silverRate && Boolean(errors.silverRate)}
                                        helperText={touched.silverRate && errors.silverRate}
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <StyledTextField
                                        fullWidth
                                        label="Labour (₹)"
                                        name="labour"
                                        type="text"
                                        value={values.labour}
                                        onChange={(e) => setFieldValue('labour', e.target.value)}
                                        error={touched.labour && Boolean(errors.labour)}
                                        helperText={touched.labour && errors.labour}
                                      />
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            )}
                          </>
                        )}

                        {/* Wastage - Hidden in MRP Mode */}
                        {!isMRPMode && values.metal !== 'Gemstone' && (
                          <Grid item xs={12} md={6}>
                            <StyledTextField
                              fullWidth
                              label="Wastage (%)"
                              name="wastage"
                              type="text"
                              value={values.wastage}
                              onChange={(e) => setFieldValue('wastage', e.target.value)}
                              error={touched.wastage && Boolean(errors.wastage)}
                              helperText={touched.wastage && errors.wastage}
                              sx={{ mb: 3 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              }}
                            />
                          </Grid>
                        )}

                        {/* Additional Information Card */}
                        <Card sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          backdropFilter: 'blur(20px)',
                          background: alpha(theme.palette.background.paper, 0.5),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          boxShadow: theme.shadows[4],
                          mb: 3,
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="h5" sx={{
                              mb: 3,
                              fontWeight: 700,
                              color: isDark ? '#e2e8f0' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}>
                              <TagIcon fontSize="small" />
                              Additional Information
                            </Typography>

                            <Grid container spacing={2}>
                              {/* Serial Number - Always visible */}
                              <Grid item xs={12} md={isMRPMode ? 12 : 6}>
                                <StyledTextField
                                  fullWidth
                                  label="Serial No."
                                  name="serialNo"
                                  type="text"
                                  value={values.serialNo}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d+$/.test(val)) {
                                      setFieldValue('serialNo', val);
                                    }
                                  }}
                                  error={touched.serialNo && Boolean(errors.serialNo)}
                                  helperText={touched.serialNo && errors.serialNo}
                                  InputProps={{
                                    sx: {
                                      backgroundColor: alpha(
                                        isDark ? theme.palette.background.paper : '#f8fafc',
                                        isDark ? 0.2 : 0.5
                                      ),
                                    }
                                  }}
                                />
                              </Grid>

                              {/* Making Charges - Only for non-MRP mode */}
                              {!isMRPMode && (
                                <Grid item xs={12} md={6}>
                                  <StyledTextField
                                    fullWidth
                                    label="Making Charges"
                                    name="makingCharges"
                                    type="text"
                                    value={values.makingCharges}
                                    onChange={(e) => setFieldValue('makingCharges', e.target.value)}
                                    error={touched.makingCharges && Boolean(errors.makingCharges)}
                                    helperText={touched.makingCharges && errors.makingCharges}
                                  />
                                </Grid>
                              )}

                              {/* Description - Always visible */}
                              <Grid item xs={12}>
                                <StyledTextField
                                  fullWidth
                                  label="Description (Optional)"
                                  name="description"
                                  value={values.description}
                                  onChange={(e) => setFieldValue('description', e.target.value)}
                                  multiline
                                  rows={3}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <DescriptionIcon sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {/* Validation Status - Only for non-MRP mode */}
                        {!isMRPMode && (
                          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#475569' }}>
                              Validation Status:
                            </Typography>
                            <Stack spacing={0.5}>
                              <Typography variant="caption" sx={{
                                color: itemWeights.every(w => w.serialNo && w.serialNo.trim() !== '')
                                  ? 'success.main'
                                  : 'error.main'
                              }}>
                                {itemWeights.every(w => w.serialNo && w.serialNo.trim() !== '')
                                  ? '✅ All serial numbers assigned'
                                  : '⚠️ Some items missing serial numbers'}
                              </Typography>
                              {serialValidationErrors.hasDuplicates && (
                                <Typography variant="caption" sx={{ color: 'error.main' }}>
                                  ⚠️ Duplicate serial numbers: {serialValidationErrors.duplicateNumbers.join(', ')}
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{
                                color: values.name ? 'success.main' : 'error.main'
                              }}>
                                {values.name ? '✅ Item name selected' : '⚠️ Item name required'}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: values.metal ? 'success.main' : 'error.main'
                              }}>
                                {values.metal ? '✅ Metal type selected' : '⚠️ Metal type required'}
                              </Typography>
                              <Typography variant="caption" sx={{
                                color: values.supplierName ? 'success.main' : 'error.main'
                              }}>
                                {values.supplierName ? '✅ Supplier name entered' : '⚠️ Supplier name required'}
                              </Typography>
                              {values.metal && values.name && values.serialNo && (
                                <Typography variant="caption" sx={{ color: 'success.main' }}>
                                  ✅ Serial number: {values.serialNo}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                      </>
                    )}

                    {/* Action Buttons */}
                    {!isGrossWeightProduct && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                          type="button"
                          variant="contained"
                          disabled={isSubmitting || formikSubmitting || isUpdatingMasterData}
                          startIcon={isSubmitting ? <CircularProgress size={20} /> : <QrCodeIcon />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (isMRPMode) {
                              if (!values.mrp || parseFloat(values.mrp) <= 0) {
                                alert('❌ Please enter a valid MRP');
                                return;
                              }
                              if (!values.name) {
                                alert('❌ Please select an item name');
                                return;
                              }
                              if (!values.metal) {
                                alert('❌ Please select a metal type');
                                return;
                              }
                              if (!values.supplierName) {
                                alert('❌ Please enter a supplier name');
                                return;
                              }
                              if (!values.serialNo) {
                                alert('❌ Please enter a serial number');
                                return;
                              }
                            } else {
                              const validationErrors = validateBeforeSubmit(values);
                              if (validationErrors.length > 0) {
                                alert(`❌ Please fix these issues before submitting:\n\n${validationErrors.join('\n')}`);
                                return;
                              }
                            }
                            submitForm();
                          }}
                          sx={{
                            borderRadius: 3,
                            fontWeight: 600,
                            textTransform: 'none',
                            background: isMRPMode
                              ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                              : isDark
                                ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            boxShadow: isMRPMode
                              ? '0 4px 14px 0 rgba(5, 150, 105, 0.35)'
                              : isDark
                                ? '0 4px 14px 0 rgba(59, 130, 246, 0.35)'
                                : '0 4px 14px 0 rgba(16, 185, 129, 0.35)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              background: isMRPMode
                                ? 'linear-gradient(135deg, #047857 0%, #065f46 100%)'
                                : isDark
                                  ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)'
                                  : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              transform: 'translateY(-2px)'
                            },
                            '&.Mui-disabled': {
                              background: alpha(
                                isMRPMode ? '#059669' : (isDark ? '#3b82f6' : '#10b981'),
                                0.3
                              ),
                              boxShadow: 'none',
                            }
                          }}
                        >
                          {isSubmitting
                            ? 'Processing...'
                            : isMRPMode
                              ? 'Add MRP Item & Generate QR'
                              : `Add ${itemWeights.length} Item${itemWeights.length > 1 ? 's' : ''} & Generate QR`}
                        </Button>

                        <Button
                          type="button"
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={() => handleReset(resetForm)}
                          disabled={isUpdatingMasterData}
                          sx={{
                            borderRadius: 3,
                            fontWeight: 600,
                            textTransform: 'none',
                            backdropFilter: 'blur(10px)',
                            background: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            color: isDark ? '#e2e8f0' : '#475569',
                            border: `1.5px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.2 : 0.5
                            )}`,
                            px: 4,
                            py: 1.5,
                            '&:hover': {
                              background: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.5 : 0.9
                              ),
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          Reset Form
                        </Button>
                      </Box>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </Grid>

          {/* QR Code Display Section */}
          <Grid item xs={12} md={4}>
            {showQR && lastAddedItem && (
              <Card sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                background: alpha(theme.palette.background.paper, 0.7),
                border: `1px solid ${alpha(
                  isDark ? theme.palette.divider : '#e2e8f0',
                  isDark ? 0.2 : 0.3
                )}`,
                boxShadow: theme.shadows[4],
                position: 'sticky',
                top: 20,
              }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{
                    mb: 3,
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}>
                    <QrCodeIcon fontSize="small" />
                    Generated QR Codes ({lastAddedItem?.createdItems?.length || 1})
                  </Typography>

                  <Box sx={{
                    maxHeight: '450px',
                    overflowY: 'auto',
                    mb: 3,
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: alpha(isDark ? '#1e293b' : '#e2e8f0', 0.1),
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: alpha('#8B5CF6', 0.3),
                      borderRadius: '10px',
                    },
                  }}>
                    {(lastAddedItem?.createdItems || [lastAddedItem]).map((item, index) => {
                      const qrCode = item.qr || `JWL${Date.now()}-${index + 1}`;
                      const qrImageData = qrImages[qrCode] || null;

                      return (
                        <Paper
                          key={index}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            background: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            border: `1px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.2 : 0.3
                            )}`,
                          }}
                        >
                          <Typography variant="caption" sx={{
                            display: 'block',
                            color: isDark ? '#94a3b8' : '#64748b',
                            fontWeight: 600,
                            mb: 1,
                            textAlign: 'left',
                          }}>
                            Item #{index + 1} {isMRPMode ? `- MRP: ₹${formatNumber(item.mrp || lastAddedItem?.mrp)}` : `- Serial No: ${item.serialNo || 'N/A'}`}
                          </Typography>

                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}>
                            {qrImageData ? (
                              <img
                                src={qrImageData}
                                alt={`QR Code for Item ${index + 1}`}
                                style={{
                                  width: '100%',
                                  maxWidth: '150px',
                                  height: 'auto',
                                  borderRadius: '8px',
                                  marginBottom: '8px',
                                }}
                              />
                            ) : (
                              <Box sx={{
                                width: '150px',
                                height: '150px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: alpha(isDark ? '#1e293b' : '#f1f5f9', 0.5),
                                borderRadius: '8px',
                                marginBottom: '8px',
                              }}>
                                <CircularProgress size={30} />
                              </Box>
                            )}
                            <Typography variant="caption" sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '10px',
                              wordBreak: 'break-all',
                              fontWeight: 600,
                            }}>
                              QR: {qrCode}
                            </Typography>
                            {isMRPMode ? (
                              <Typography variant="caption" sx={{
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontSize: '10px',
                              }}>
                                MRP: ₹{formatNumber(item.mrp || lastAddedItem?.mrp)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontSize: '10px',
                              }}>
                                Serial: {item.serialNo || 'N/A'} | Price: ₹{item.price || '0'}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>

                  {lastAddedItem && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      background: alpha(
                        isDark ? theme.palette.background.paper : '#f8fafc',
                        isDark ? 0.3 : 0.5
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      mb: 3,
                    }}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 600,
                        color: isDark ? '#e2e8f0' : '#475569',
                        mb: 1,
                      }}>
                        {isMRPMode ? 'MRP Item Added Successfully!' : 'Items Added Successfully!'}
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                          {lastAddedItem.name} - {lastAddedItem.metal}
                        </Typography>
                        {isMRPMode ? (
                          <>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                              MRP: ₹{formatNumber(lastAddedItem.mrp)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                              Serial No: {lastAddedItem.serialNo}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                              Total Items: {lastAddedItem?.createdItems?.length || lastAddedItem?.itemWeights?.length || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>
                              Serial Numbers: {(lastAddedItem?.createdItems || lastAddedItem?.itemWeights || []).map(w => w.serialNo).filter(Boolean).join(', ') || 'N/A'}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrintLabel(lastAddedItem)}
                    disabled={!lastAddedItem || isGrossWeightProduct || isUpdatingMasterData}
                    sx={{
                      borderRadius: 3,
                      fontWeight: 600,
                      textTransform: 'none',
                      width: '100%',
                      background: isDark
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                        : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      boxShadow: '0 4px 20px rgba(139, 92, 246, 0.25)',
                      py: 1.5,
                      '&:hover': {
                        boxShadow: '0 6px 24px rgba(139, 92, 246, 0.35)',
                        transform: 'translateY(-2px)',
                      },
                      '&.Mui-disabled': {
                        background: alpha(
                          isDark ? '#8b5cf6' : '#8b5cf6',
                          0.3
                        ),
                        boxShadow: 'none',
                      }
                    }}
                  >
                    {isGrossWeightProduct
                      ? 'Print Not Available'
                      : isMRPMode
                        ? 'Print MRP Label'
                        : `Print ${lastAddedItem?.createdItems?.length || lastAddedItem?.itemWeights?.length || 1} Label${(lastAddedItem?.createdItems?.length || lastAddedItem?.itemWeights?.length || 1) > 1 ? 's' : ''}`}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Dialogs for managing master data */}
      {/* Item Categories Management Dialog */}
      <Dialog
        open={itemDialogOpen}
        onClose={() => !isUpdatingMasterData && setItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(20px)',
            background: alpha(theme.palette.background.paper, 0.95),
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Item Categories</Typography>
          <IconButton onClick={() => !isUpdatingMasterData && setItemDialogOpen(false)} disabled={isUpdatingMasterData}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Item Name"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                disabled={isUpdatingMasterData}
                sx={{ flex: 2, minWidth: '200px' }}
              />
              <TextField
                size="small"
                placeholder="Shortcut (max 3)"
                value={newItemShortcut}
                onChange={(e) => setNewItemShortcut(e.target.value.toUpperCase().slice(0, 3))}
                disabled={isUpdatingMasterData}
                sx={{ flex: 1, minWidth: '100px' }}
                inputProps={{ maxLength: 3 }}
              />
              <Button
                variant="contained"
                onClick={editingItem ? handleUpdateItemCategory : handleAddItemCategory}
                disabled={!newItemName || !newItemShortcut || isUpdatingMasterData}
                sx={{ borderRadius: 2 }}
              >
                {editingItem ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Items</Typography>
          <List>
            {itemCategories.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={item.name}
                  secondary={`Shortcut: ${item.shortcut}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingItem(item);
                      setNewItemName(item.name);
                      setNewItemShortcut(item.shortcut);
                      setItemDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteItemCategory(item.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Metal Types Management Dialog */}
      <Dialog
        open={metalDialogOpen}
        onClose={() => !isUpdatingMasterData && setMetalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(20px)',
            background: alpha(theme.palette.background.paper, 0.95),
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Metal Types</Typography>
          <IconButton onClick={() => !isUpdatingMasterData && setMetalDialogOpen(false)} disabled={isUpdatingMasterData}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingMetal ? 'Edit Metal' : 'Add New Metal'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Metal Name"
                value={newMetalName}
                onChange={(e) => setNewMetalName(e.target.value)}
                disabled={isUpdatingMasterData}
                sx={{ flex: 2, minWidth: '200px' }}
              />
              <TextField
                size="small"
                placeholder="Shortcut (max 2)"
                value={newMetalShortcut}
                onChange={(e) => setNewMetalShortcut(e.target.value.toUpperCase().slice(0, 2))}
                disabled={isUpdatingMasterData}
                sx={{ flex: 1, minWidth: '100px' }}
                inputProps={{ maxLength: 2 }}
              />
              <Button
                variant="contained"
                onClick={editingMetal ? handleUpdateMetalType : handleAddMetalType}
                disabled={!newMetalName || !newMetalShortcut || isUpdatingMasterData}
                sx={{ borderRadius: 2 }}
              >
                {editingMetal ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Metals</Typography>
          <List>
            {metalTypes.map((metal) => (
              <ListItem
                key={metal.id}
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={metal.name}
                  secondary={`Shortcut: ${metal.shortcut}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingMetal(metal);
                      setNewMetalName(metal.name);
                      setNewMetalShortcut(metal.shortcut);
                      setMetalDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteMetalType(metal.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      {/* Silver Types Management Dialog */}
      <Dialog
        open={silverTypeDialogOpen}
        onClose={() => !isUpdatingMasterData && setSilverTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(20px)',
            background: alpha(theme.palette.background.paper, 0.95),
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Silver Types</Typography>
          <IconButton onClick={() => !isUpdatingMasterData && setSilverTypeDialogOpen(false)} disabled={isUpdatingMasterData}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {editingSilverType ? 'Edit Silver Type' : 'Add New Silver Type'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                size="small"
                label="Silver Type Code"
                placeholder="e.g., 999silver"
                value={newSilverType}
                onChange={(e) => setNewSilverType(e.target.value)}
                disabled={isUpdatingMasterData}
                fullWidth
                helperText="Internal code (lowercase, no spaces)"
              />
              <TextField
                size="small"
                label="Display Name"
                placeholder="e.g., 999 Silver"
                value={newSilverTypeDisplayName}
                onChange={(e) => setNewSilverTypeDisplayName(e.target.value)}
                disabled={isUpdatingMasterData}
                fullWidth
                helperText="Name shown in dropdowns"
              />
              <TextField
                size="small"
                label="Shortcut (max 3)"
                placeholder="e.g., 999"
                value={newSilverTypeShortcut}
                onChange={(e) => setNewSilverTypeShortcut(e.target.value.toUpperCase().slice(0, 3))}
                disabled={isUpdatingMasterData}
                fullWidth
                inputProps={{ maxLength: 3 }}
              />
              <Button
                variant="contained"
                onClick={editingSilverType ? handleUpdateSilverType : handleAddSilverType}
                disabled={!newSilverType || isUpdatingMasterData}
                sx={{ borderRadius: 2, mt: 1 }}
              >
                {editingSilverType ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Existing Silver Types</Typography>
          <List>
            {silverTypes.map((type) => (
              <ListItem
                key={type.id}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  mb: 1,
                  borderRadius: 2,
                }}
              >
                <ListItemText
                  primary={type.displayName || type.name}
                  secondary={`Code: ${type.name} | Shortcut: ${type.shortcut || type.name.substring(0, 3).toUpperCase()}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setEditingSilverType(type);
                      setNewSilverType(type.name);
                      setNewSilverTypeDisplayName(type.displayName || '');
                      setNewSilverTypeShortcut(type.shortcut || '');
                      setSilverTypeDialogOpen(true);
                    }}
                    sx={{ mr: 1 }}
                    disabled={isUpdatingMasterData}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteSilverType(type.id)}
                    color="error"
                    disabled={isUpdatingMasterData}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddItemPage;